import ChevronLeftOutlinedIcon from '@mui/icons-material/ChevronLeftOutlined';
import ChevronRightOutlinedIcon from '@mui/icons-material/ChevronRightOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuthContext } from '../../../context/AuthContext';
import { useNotificationContext } from '../../../context/NotificationContext';
import { CsvExportControl } from '../../components/common/CsvExportControl';
import { DashboardCountedMultiSelect } from '../../components/common/DashboardCountedMultiSelect';
import { MasterLabelLink } from '../../components/common/MasterLabelLink';
import { VersionedTrendChart } from '../../components/common/VersionedTrendChart';
import { getForgeChainLabel } from '../../config/chains';
import {
  enumParam,
  pageParam,
  stringListParam,
  stringParam,
  useUrlFilterState,
} from '../../hooks/useUrlFilterState';
import { ForgeApiService } from '../../services/ForgeApiService';
import {
  CleaningChainHistoryResponse,
  CleaningChainStatsResponse,
  CleaningDashboardResponse,
  CleaningFamily,
  CleaningFamilyScore,
  CleaningFinding,
  CleaningIssueScope,
  CleaningIssueType,
  CleaningItemScore,
  CleaningItemsResponse,
  CleaningLockFilter,
  CleaningMasterLabelParams,
  CleaningRefreshRun,
  CleaningScoreFamily,
  CleaningSeverity,
  CleaningSort,
  CleaningSubscores,
} from '../../types';
import { CsvValue, downloadCsv, fetchPagedRows } from '../../utils/csvExport';
import {
  ageSecondsFromTimestamp,
  formatDuration,
  formatRelativeAge,
  formatTimestamp,
  snapshotAgeColor,
} from '../../utils/forgeDashboardFormat';
import { buildMasterLabelUrl } from '../../utils/masterLabelLinks';

const RUN_POLL_INTERVAL_MS = 5000;
const ITEMS_PAGE_SIZE = 50;
const TRAINING_ISSUE_FILTER_SCHEMA_VERSION = 5;
// ~15s of consecutive failures before giving up on a run's poll loop.
const MAX_CONSECUTIVE_POLL_FAILURES = 3;
const CLEANING_SWR_CACHE_PREFIX = 'cleaning-swr-v2:';
const CLEANING_CACHE_SLOTS = {
  dashboard: 'dashboard',
  items: 'items',
  stats: 'stats',
  history: 'history',
} as const;

const SEVERITY_ORDER: CleaningSeverity[] = ['critical', 'high', 'medium', 'low', 'clean'];

interface CleaningCacheSlot<T> {
  key: string;
  data: T;
}

function readCleaningCache<T>(scope: string | null, key: string): T | null {
  try {
    if (typeof window === 'undefined' || !scope) return null;
    const raw = window.sessionStorage.getItem(
      `${CLEANING_SWR_CACHE_PREFIX}${encodeURIComponent(scope)}:${key}`
    );
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function writeCleaningCache(scope: string | null, key: string, value: unknown): void {
  try {
    if (typeof window === 'undefined' || !scope) return;
    window.sessionStorage.setItem(
      `${CLEANING_SWR_CACHE_PREFIX}${encodeURIComponent(scope)}:${key}`,
      JSON.stringify(value)
    );
  } catch {
    // sessionStorage may be disabled or full; caching is best-effort only.
  }
}

function readCleaningCacheEntry<T>(
  scope: string | null,
  slot: string,
  requestKey: string
): CleaningCacheSlot<T> | null {
  const cached = readCleaningCache<CleaningCacheSlot<T>>(scope, slot);
  return cached?.key === requestKey ? cached : null;
}

function readCleaningCacheSlot<T>(
  scope: string | null,
  slot: string,
  requestKey: string
): T | null {
  return readCleaningCacheEntry<T>(scope, slot, requestKey)?.data ?? null;
}

function writeCleaningCacheSlot<T>(
  scope: string | null,
  slot: string,
  requestKey: string,
  data: T
): void {
  writeCleaningCache(scope, slot, { key: requestKey, data });
}

function cleaningItemsRequestKey(params: {
  chainId: number;
  family: CleaningFamily;
  severity: CleaningSeverity | 'all';
  excluded: 'hide' | 'only';
  duplicates: 'hide' | 'show';
  issueTypes: CleaningIssueType[];
  issueScope: CleaningIssueScope | 'all';
  locks: CleaningLockFilter;
  modPrefixes: string[];
  focusSubscores: string[];
  committedQuery: string;
  sort: CleaningSort;
  page: number;
}): string {
  // The free-text query is encoded so a value containing ':' cannot collide
  // with a different field layout in the cache key.
  return `${params.chainId}:${params.family}:${params.severity}:${params.excluded}:${params.duplicates}:${params.issueTypes.join(',')}:${params.issueScope}:${params.locks}:${params.modPrefixes.join(',')}:${params.focusSubscores.join(',')}:${encodeURIComponent(
    params.committedQuery
  )}:${params.sort}:${params.page}`;
}

const SEVERITY_COLORS: Record<CleaningSeverity, string> = {
  critical: '#e8523f',
  high: '#eea337',
  medium: '#f2cc46',
  low: '#8ab746',
  clean: '#41c35b',
};

const SEVERITY_TAB_OPTIONS: Array<{ value: CleaningSeverity | 'all'; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
  { value: 'clean', label: 'Clean' },
];

type TrainingIssueMetadata = {
  code: string;
  label: string;
  message: string;
  fullGroupMessage?: string;
};

const TRAINING_ISSUE_METADATA: Record<CleaningIssueType, TrainingIssueMetadata> = {
  different_groups: {
    code: 'T1a',
    label: 'Different groups',
    message: 'Blue and purple Crops look different.',
    fullGroupMessage: 'The full Design group between blue and purple looks incorrect.',
  },
  stray_crops: {
    code: 'T1b',
    label: 'Stray Crops',
    message: 'Orange Crops do not match the others.',
  },
  crops_do_not_match: {
    code: 'T1c',
    label: 'Crops do not match',
    message: 'Orange Crops do not match this Design.',
    fullGroupMessage: 'Orange Crops do not match this full group.',
  },
  similar_designs: {
    code: 'T2',
    label: 'Similar Designs',
    message: 'These Training classes look too similar.',
  },
  wrong_design: {
    code: 'T1+T2',
    label: 'Wrong Design',
    message: 'This Crop may be in the wrong Design.',
  },
};

const TRAINING_ISSUE_TYPE_OPTIONS: Array<{
  value: CleaningIssueType;
  label: string;
}> = [
  ...(
    Object.entries(TRAINING_ISSUE_METADATA) as Array<[CleaningIssueType, TrainingIssueMetadata]>
  ).map(([value, metadata]) => ({ value, label: metadata.label })),
];

const TRAINING_ISSUE_SCOPE_OPTIONS: Array<{
  value: CleaningIssueScope | 'all';
  label: string;
}> = [
  { value: 'all', label: 'All scopes' },
  { value: 'one_design', label: 'One Design' },
  { value: 'full_group', label: 'Full group' },
];

const TRAINING_ISSUE_SCOPE_LABELS: Record<CleaningIssueScope, string> = {
  one_design: 'One Design',
  full_group: 'Full group',
};

function trainingIssueMetadata(issueType?: string | null): TrainingIssueMetadata | undefined {
  if (!issueType) return undefined;
  return (TRAINING_ISSUE_METADATA as Record<string, TrainingIssueMetadata | undefined>)[issueType];
}

const EXCLUDED_FILTER_OPTIONS: Array<{ value: 'hide' | 'only'; label: string }> = [
  { value: 'hide', label: 'Open' },
  { value: 'only', label: 'No changes needed' },
];

type SubscoreGroupKey = CleaningScoreFamily;

interface SubscoreGroupMeta {
  key: SubscoreGroupKey;
  label: string;
  stripLabel: string;
  description: string;
  color: string;
}

const SUBSCORE_GROUPS: SubscoreGroupMeta[] = [
  {
    key: 'training',
    label: 'Training',
    stripLabel: 'Training',
    description:
      'Training-class cleanliness: the Crop sets and identities used to train recognition',
    color: '#5b8def',
  },
  {
    key: 'inference',
    label: 'Inference',
    stripLabel: 'Inference',
    description:
      'Non-front and carton ambiguity, plus visual validation of partial and non-front groups',
    color: '#a06bfa',
  },
  {
    key: 'catalog',
    label: 'Catalog',
    stripLabel: 'Catalog',
    description: 'Resolution, assignment, review, and metadata workflow checks',
    color: '#2fa47a',
  },
];

const FAMILY_OPTIONS: Array<{
  key: CleaningFamily;
  label: string;
  description: string;
}> = [
  {
    key: 'combined',
    label: 'Combined',
    description:
      'Weighted overall cleanliness, with Training, Inference, and Catalog shown together',
  },
  ...SUBSCORE_GROUPS.map(({ key, label, description }) => ({ key, label, description })),
];

const FAMILY_LABELS: Record<CleaningFamily, string> = {
  combined: 'Combined',
  training: 'Training',
  inference: 'Inference',
  catalog: 'Catalog',
};

const SUBSCORE_GROUPS_BY_KEY: Record<SubscoreGroupKey, SubscoreGroupMeta> = SUBSCORE_GROUPS.reduce(
  (acc, group) => {
    acc[group.key] = group;
    return acc;
  },
  {} as Record<SubscoreGroupKey, SubscoreGroupMeta>
);

interface SubscoreMetaEntry {
  key: keyof CleaningSubscores;
  sort: CleaningSort;
  group: SubscoreGroupKey;
  color: string;
  weight: number;
  secondary?: boolean;
  /** Debug-only readout that explains a scored axis without adding weight. */
  diagnostic?: 'split' | 'strays' | 'fragmentation';
  short: string;
  full: string;
  meaning: string;
  formula: string[];
  highlights: string;
  fix: string;
  example?: string;
}

const SUBSCORE_META: SubscoreMetaEntry[] = [
  {
    key: 'classCoherence',
    color: '#4e79a7',
    sort: 'class_coherence',
    group: 'training',
    weight: 1,
    short: 'T1 · Coherence',
    full: 'Training-class coherence',
    meaning:
      'All front Crops inside one Training class should describe the same product identity. Full Design groups are merged before this check.',
    formula: [
      'one Crop -> 100; similar Crops in one coherent cluster -> 100',
      'Split: two or more coherent peer groups; value = 100 × mean similarity between the two peer evidence groups',
      'Strays: one coherent group plus singleton outsiders; value = the worst Crop’s calibrated fit to the coherent group',
      'Fragmentation: no coherent group; value = 100 × the mean closest-peer similarity across Crops',
      'exactly one diagnosis explains a low Coherence score',
    ],
    highlights:
      'Split shows two equal-rank groups; Strays show outside Crops against the core; Fragmentation shows the whole class.',
    fix: 'Correct Crop placement, Training-class boundaries, orientation, or a mistaken full Design group.',
    example:
      'Example: two peer groups with mean similarity 0.50 give Coherence 50. A single front Crop gives 100 because there is nothing inside its Training class to disagree with.',
  },
  {
    key: 'classConfusion',
    color: '#f28e2b',
    sort: 'class_confusion',
    group: 'training',
    weight: 1,
    short: 'T2 · Confusion',
    full: 'Between-class confusion',
    meaning:
      'Two distinct Training classes look unexpectedly similar, regardless of whether their Item numbers happen to match.',
    formula: [
      'full Design groups already form one Training class',
      'compare coherent cores so a Stray cannot contaminate a clean neighboring class',
      'partial Design groups stay separate but their expected similarity is exempt here',
      'closest eligible class is graded through 0.55/0.80/0.90/0.97 similarity anchors',
      'Item number is shown only as remediation context',
    ],
    highlights: 'Blue and purple: two similar Training classes shown as equal-rank evidence.',
    fix: 'Correct assignments or declare the appropriate full or partial Design group.',
    example:
      'Example: a closest eligible class similarity of about 0.862 lies between the 0.80 → 82 and 0.90 → 30 anchors, giving Confusion about 50.',
  },
  {
    key: 'nonfrontConfusion',
    color: '#76b7b2',
    sort: 'nonfront_confusion',
    group: 'inference',
    weight: 0.5,
    short: 'I1 · Non-front',
    full: 'Non-front confusion',
    meaning: 'A non-front Crop is too similar to a different ungrouped item.',
    formula: ['Closest cross-item non-front class is graded through 0.75/0.85/0.92/0.97 anchors.'],
    highlights: 'Blue: this non-front view · Purple: the competing view.',
    fix: 'Correct the label or declare the appropriate group.',
    example:
      'Example: similarity about 0.897 lies two-thirds of the way from 0.85 → 80 to 0.92 → 35, giving about 50.',
  },
  {
    key: 'cartonConfusion',
    color: '#edc948',
    sort: 'carton_confusion',
    group: 'inference',
    weight: 0.5,
    short: 'I2 · Carton',
    full: 'Carton confusion',
    meaning: 'This item’s carton is too similar to another item’s carton.',
    formula: ['Closest cross-item carton class is graded through 0.90/0.95/0.98/0.995 anchors.'],
    highlights: 'Blue: this carton · Purple: the competing carton.',
    fix: 'Group equivalent products or correct the carton barcode.',
    example:
      'Example: carton similarity about 0.969 lies between 0.95 → 75 and 0.98 → 35, giving about 50.',
  },
  {
    key: 'partialGroupFrontAlignment',
    color: '#9c6ade',
    sort: 'partial_group_front_alignment',
    group: 'inference',
    weight: 0.5,
    short: 'I3 · Partial group',
    full: 'Partial-group front alignment',
    meaning:
      'Designs declared as a partial group should have genuinely similar fronts, while remaining separate training classes.',
    formula: [
      'full-grouped designs first collapse into the same training class',
      'compare the front centroids of each distinct class inside the partial group',
      'value = scale(similarity, 0.50 -> 10, 0.90 -> 100)',
    ],
    highlights: 'Red: this side of the weak partial relation · Blue: the declared counterpart.',
    fix: 'Remove or correct the partial group if these fronts are not truly similar.',
    example:
      'Example: similarity about 0.678 maps between 0.50 → 10 and 0.90 → 100, giving about 50.',
  },
  {
    key: 'nonfrontGroupAlignment',
    color: '#b07aa1',
    sort: 'nonfront_group_alignment',
    group: 'inference',
    weight: 0.5,
    short: 'I4 · Non-front group',
    full: 'Non-front-group alignment',
    meaning:
      'Designs declared as a non-front group should share a recognizably similar non-front appearance.',
    formula: [
      'compare every non-front crop across each declared design pair',
      'use the mean of the strongest 10% of cross-crop similarities',
      'value = scale(similarity, 0.30 -> 10, 0.80 -> 100)',
    ],
    highlights: 'Red: this design’s non-fronts · Blue: the declared counterpart’s non-fronts.',
    fix: 'Remove or correct the non-front group if no shared non-front appearance exists.',
    example:
      'Example: strongest-view similarity about 0.522 maps between 0.30 → 10 and 0.80 → 100, giving about 50.',
  },
  {
    key: 'identityResolution',
    color: '#17becf',
    sort: 'identity_resolution',
    group: 'catalog',
    weight: 0.05,
    short: 'C1 · Resolution',
    full: 'Item-number resolution',
    meaning: 'A normalized barcode does not resolve uniquely to one item number.',
    formula: ['all barcodes resolve uniquely -> 100; partial/ambiguous -> 50; none -> 0'],
    highlights: 'Visual context only; the catalog link is the task.',
    fix: 'Repair barcode-to-item-number resolution.',
    example:
      'Example: if some normalized barcodes resolve uniquely but another is ambiguous, Resolution is 50.',
  },
  {
    key: 'assignment',
    color: '#c2932e',
    sort: 'assignment',
    group: 'catalog',
    weight: 0.05,
    short: 'C2 · Assignment',
    full: 'Unassigned crops',
    meaning: 'Product crops are waiting for design assignment.',
    formula: ['pending crops weigh 2x active and front crops weigh 2x non-front crops'],
    highlights: 'Red: crops awaiting assignment.',
    fix: 'Assign the crops in Master Label.',
    example:
      'Example: 4 weighted assigned units and 5 weighted unassigned units give 10 + 90 × (4 / 9) = 50.',
  },
  {
    key: 'reviewFlags',
    color: '#bc6c9c',
    sort: 'review_flags',
    group: 'catalog',
    weight: 0.05,
    short: 'C3 · Review',
    full: 'Review flags',
    meaning: 'Known crop issues are still awaiting action.',
    formula: ['Base penalty plus a ratio penalty; overall weight 0.05.'],
    highlights: 'Red: crops carrying review flags.',
    fix: 'Resolve the flagged crops.',
    example:
      'Example: about 9 flagged Crops out of 34 tracked Crops gives a ratio near 0.265 and a score near 50.',
  },
  {
    key: 'metadataIntegrity',
    color: '#8c6d31',
    sort: 'metadata_integrity',
    group: 'catalog',
    weight: 0.05,
    short: 'C4 · Metadata',
    full: 'Metadata contradictions',
    meaning: 'A crop has contradictory active/pending state or a carton is assigned to a design.',
    formula: ['no tracked crops -> unavailable; no contradiction -> 100; any contradiction -> 50'],
    highlights: 'Red: crops with contradictory metadata.',
    fix: 'Correct the crop state or classification metadata.',
    example:
      'Example: one Crop with contradictory active/pending state makes Metadata 50; no contradiction gives 100.',
  },
];

// Training still has exactly two scored axes. These mutually exclusive
// readouts expose how T1 Coherence was diagnosed so calibrations can be
// inspected and sorted without counting the same concern more than once.
const TRAINING_COHERENCE_DIAGNOSTIC_META: SubscoreMetaEntry[] = [
  {
    key: 'frontSplit',
    color: '#4e79a7',
    sort: 'front_split',
    group: 'training',
    weight: 0,
    diagnostic: 'split',
    short: 'T1a · Split',
    full: 'Split diagnostic',
    meaning: 'The selected Training Class contains two or more coherent peer groups.',
    formula: ['100 × mean similarity between the two peer evidence groups'],
    highlights: 'Two equal-rank peer groups.',
    fix: 'Review both groups and correct the Training-class boundary.',
  },
  {
    key: 'frontStrays',
    color: '#59a14f',
    sort: 'front_strays',
    group: 'training',
    weight: 0,
    diagnostic: 'strays',
    short: 'T1b · Strays',
    full: 'Strays diagnostic',
    meaning: 'The selected Training Class has a coherent group plus one or more outside Crops.',
    formula: ['the worst Crop’s calibrated fit to the coherent group'],
    highlights: 'Outside Crops against the coherent group.',
    fix: 'Correct the outside Crops or the Training-class boundary.',
  },
  {
    key: 'frontFragmentation',
    color: '#bc80bd',
    sort: 'front_fragmentation',
    group: 'training',
    weight: 0,
    diagnostic: 'fragmentation',
    short: 'T1c · Fragmentation',
    full: 'Fragmentation diagnostic',
    meaning: 'The selected Training Class has no coherent group.',
    formula: ['100 × the mean closest-peer similarity across Crops'],
    highlights: 'The whole fragmented Training Class.',
    fix: 'Review the whole class and correct its boundary or Crop placement.',
  },
];

// Preserve the exact Training breakdown for snapshots calculated before the
// two-axis v3 model. Old rows remain useful evidence, but are visibly greyed
// and labelled with their old score version elsewhere in the dashboard.
const TWO_AXIS_TRAINING_SCHEMA_VERSION = 3;

const LEGACY_TRAINING_SUBSCORE_META: SubscoreMetaEntry[] = [
  {
    key: 'frontSplit',
    color: '#4e79a7',
    sort: 'front_split',
    group: 'training',
    weight: 1,
    short: 'T1 · Split',
    full: 'Split front class',
    meaning: 'Historical split score from the selected old heuristic version.',
    formula: ['Historical score; refresh to use current Coherence.'],
    highlights: 'Two peer groups.',
    fix: 'Review both groups.',
  },
  {
    key: 'frontFragmentation',
    color: '#bc80bd',
    sort: 'front_fragmentation',
    group: 'training',
    weight: 1,
    short: 'T2 · Fragmentation',
    full: 'Fragmented front class',
    meaning: 'Historical fragmentation score from the selected old heuristic version.',
    formula: ['Historical score; refresh to use current Coherence.'],
    highlights: 'The fragmented class.',
    fix: 'Review the whole class.',
  },
  {
    key: 'frontStrays',
    color: '#59a14f',
    sort: 'front_strays',
    group: 'training',
    weight: 1,
    short: 'T3 · Strays',
    full: 'Stray front crops',
    meaning: 'Historical Strays score from the selected old heuristic version.',
    formula: ['Historical score; refresh to use current Coherence.'],
    highlights: 'Strays against the main group.',
    fix: 'Review the highlighted Crops.',
  },
  {
    key: 'frontConfusion',
    color: '#f28e2b',
    sort: 'front_confusion',
    group: 'training',
    weight: 1,
    short: 'T4 · Confusion',
    full: 'Front class confusion',
    meaning: 'Historical cross-Item-number confusion score.',
    formula: ['Historical score; refresh to use current Training-class Confusion.'],
    highlights: 'Two similar classes.',
    fix: 'Review both classes.',
  },
  {
    key: 'duplicateFrontClasses',
    color: '#e15759',
    sort: 'duplicate_front_classes',
    group: 'training',
    weight: 1,
    short: 'T5 · Duplicates',
    full: 'Duplicate front classes',
    meaning: 'Historical same-Item-number duplicate-class score.',
    formula: ['Historical score; this is part of current Training-class Confusion.'],
    highlights: 'Two similar classes.',
    fix: 'Review both classes.',
  },
];

function subscoreMetaForVersion(
  scoreSchemaVersion?: number | null,
  scoreVersion?: string | null,
  currentScoreVersion?: string | null
): SubscoreMetaEntry[] {
  const usesLegacyTrainingSchema =
    scoreSchemaVersion != null
      ? scoreSchemaVersion < TWO_AXIS_TRAINING_SCHEMA_VERSION
      : Boolean(scoreVersion && currentScoreVersion && scoreVersion !== currentScoreVersion);
  if (usesLegacyTrainingSchema) {
    return [
      ...LEGACY_TRAINING_SUBSCORE_META,
      ...SUBSCORE_META.filter((entry) => entry.group !== 'training'),
    ];
  }
  return SUBSCORE_META;
}

function subscoreMetaByGroupForVersion(
  scoreSchemaVersion?: number | null,
  scoreVersion?: string | null,
  currentScoreVersion?: string | null
): Record<SubscoreGroupKey, SubscoreMetaEntry[]> {
  const entries = subscoreMetaForVersion(scoreSchemaVersion, scoreVersion, currentScoreVersion);
  return SUBSCORE_GROUPS.reduce(
    (acc, group) => {
      acc[group.key] = entries.filter((entry) => entry.group === group.key);
      return acc;
    },
    {} as Record<SubscoreGroupKey, SubscoreMetaEntry[]>
  );
}

const LEGACY_COHESION_STATS_META: SubscoreMetaEntry = {
  key: 'h1Cohesion',
  color: '#9467bd',
  sort: 'score',
  group: 'training',
  weight: 1,
  short: 'L · Consistency',
  full: 'Legacy consistency',
  meaning: 'A retired heuristic-version check retained only for historical statistics.',
  formula: [],
  highlights: 'Historical statistic only.',
  fix: 'Refresh to score this chain with the current task-family model.',
};

const SUBSCORE_META_BY_GROUP: Record<SubscoreGroupKey, SubscoreMetaEntry[]> =
  SUBSCORE_GROUPS.reduce(
    (acc, group) => {
      acc[group.key] = SUBSCORE_META.filter((entry) => entry.group === group.key);
      return acc;
    },
    {} as Record<SubscoreGroupKey, SubscoreMetaEntry[]>
  );

/** "T3 · Strays" -> "T3": the stable index used by the compact strip cells. */
function subscoreIndexLabel(entry: SubscoreMetaEntry): string {
  return entry.short.split(' · ')[0];
}

/** "T3 · Strays" -> "Strays": the short check name shown under the header index. */
function subscoreShortName(entry: SubscoreMetaEntry): string {
  return entry.short.split(' · ')[1] ?? entry.short;
}

/** Chain-level mean score color: red below 90, yellow to 95, green above. */
function meanScoreColor(value: number | null | undefined): string {
  if (value === null || value === undefined) return 'var(--text-primary)';
  if (value < 90) return '#e8523f';
  if (value <= 95) return '#eea337';
  return '#2fa47a';
}

const LEGACY_SUBSCORE_KEYS: Partial<Record<keyof CleaningSubscores, keyof CleaningSubscores>> = {
  classCoherence: 'frontSplit',
  classConfusion: 'frontConfusion',
  frontSplit: 'h1Split',
  frontStrays: 'h1Stray',
  frontConfusion: 'h2Confusion',
  duplicateFrontClasses: 'intraItemDistinctness',
  identityResolution: 'identity',
  reviewFlags: 'reviewLoad',
};

const LEGACY_STATS_KEYS: Partial<Record<CleaningSort, string>> = {
  class_coherence: 'front_split',
  class_confusion: 'front_confusion',
  front_split: 'h1_split',
  front_strays: 'h1_stray',
  front_confusion: 'h2_confusion',
  duplicate_front_classes: 'intra_item_distinctness',
  identity_resolution: 'identity',
  review_flags: 'review_load',
};

function subscoreValueForItem(
  item: CleaningItemScore,
  meta: SubscoreMetaEntry
): number | null | undefined {
  const current = item.subscores[meta.key];
  if (current !== null && current !== undefined) return current;
  const legacyKey = LEGACY_SUBSCORE_KEYS[meta.key];
  return legacyKey ? item.subscores[legacyKey] : current;
}

/**
 * The score whose links a focused row should use.
 *
 * A row surfaced by a focus filter is there because of that check, so its Master Label
 * link and finding must describe it rather than the item's worst check. The table and
 * the CSV both call this — when only the table did, exports silently kept pointing at
 * the worst finding.
 */
/** A perfect item has no work in it; everything below is exportable. */
const CLEANING_PERFECT_SCORE = 100;

function focusedFinding(
  item: CleaningItemScore,
  familyScore: CleaningFamilyScore,
  focusSubscores: readonly string[]
): CleaningFinding | undefined {
  if (focusSubscores.length === 0) return undefined;
  const wanted = new Set(focusSubscores);
  return (
    (item.findings ?? []).find(
      (finding) => finding.subscore && wanted.has(finding.subscore) && finding.masterLabelParams
    ) ??
    (familyScore.finding?.subscore &&
    wanted.has(familyScore.finding.subscore) &&
    familyScore.finding.masterLabelParams
      ? familyScore.finding
      : undefined)
  );
}

function focusedFamilyScore(
  item: CleaningItemScore,
  familyScore: CleaningFamilyScore,
  focusSubscores: readonly string[]
): CleaningFamilyScore {
  const focused = focusedFinding(item, familyScore, focusSubscores);
  if (!focused?.masterLabelParams) return familyScore;
  return {
    ...familyScore,
    // Score and severity stay the family's: they are what ranked the row.
    worstSubscore: focused.subscore || familyScore.worstSubscore,
    finding: focused,
    masterLabelParams: focused.masterLabelParams,
  };
}

function familyScoreForItem(item: CleaningItemScore, family: CleaningFamily): CleaningFamilyScore {
  if (family === 'combined') {
    return {
      score: item.score,
      severity: item.severity,
      worstSubscore: item.worstSubscore,
      finding: item.finding,
      masterLabelParams: item.masterLabelParams,
    };
  }
  const stored = item.familyScores?.[family];
  if (stored) return stored;
  if (family === 'training') {
    return {
      score: item.score,
      severity: item.severity,
      worstSubscore: item.worstSubscore,
      finding: item.finding,
      masterLabelParams: item.masterLabelParams,
    };
  }

  const entries = SUBSCORE_META_BY_GROUP[family];
  const effective = entries.map((entry) => ({
    entry,
    value: subscoreValueForItem(item, entry) ?? 100,
  }));
  const totalWeight = entries.reduce((sum, entry) => sum + entry.weight, 0);
  const score =
    totalWeight > 0
      ? effective.reduce((sum, { entry, value }) => sum + entry.weight * value, 0) / totalWeight
      : item.score;
  const worst = [...effective].sort((left, right) => left.value - right.value)[0]?.entry.sort;
  const finding = (item.findings ?? []).find((candidate) => candidate.subscore === worst) ?? null;
  const severity: CleaningSeverity =
    score < 75
      ? 'critical'
      : score < 82
        ? 'high'
        : score < 90
          ? 'medium'
          : score < 97
            ? 'low'
            : 'clean';
  return {
    score,
    severity,
    worstSubscore: worst ?? item.worstSubscore,
    finding,
    masterLabelParams:
      finding?.masterLabelParams ?? (item.findings === undefined ? item.masterLabelParams : null),
  };
}

/** Targeted Master Label deep link for cleaning params, or null without params. */
function masterLabelUrlForParams(
  chainId: number,
  params: CleaningMasterLabelParams | null | undefined
): string | null {
  if (!params) return null;
  return buildMasterLabelUrl({
    chainId,
    itemNumber: params.itemNumber,
    extraItemNumbers: params.secondaryItemNumber ? [params.secondaryItemNumber] : undefined,
    normalizedBarcode: params.normalizedBarcode,
    extraNormalizedBarcodes: params.secondaryNormalizedBarcode
      ? [params.secondaryNormalizedBarcode]
      : undefined,
    cropIds: params.cropIds,
    secondaryCropIds: params.secondaryCropIds,
    tertiaryCropIds: params.tertiaryCropIds,
    highlightMode: params.highlightMode ?? undefined,
    highlightScope: params.highlightScope ?? undefined,
  });
}

/** The item's targeted Master Label deep link, or null without params. */
function masterLabelUrlForItem(
  item: CleaningItemScore,
  family: CleaningFamily,
  score: CleaningFamilyScore = familyScoreForItem(item, family)
): string | null {
  return masterLabelUrlForParams(item.chainId, score.masterLabelParams);
}

const CSV_EXPORT_PAGE_SIZE = 1000;

const CSV_HEADERS: string[] = [
  'item_key',
  'item_number',
  'normalized_barcodes',
  'score',
  'severity',
  'worst_check',
  ...SUBSCORE_META.map((meta) => meta.sort),
  'finding',
  'other_item_key',
  'similarity',
  'designs',
  'active_fronts',
  'unassigned',
  'pending',
  'cartons',
  'review_flagged',
  'reason_codes',
  'master_label_url',
  'chain_id',
  'score_version',
  'computed_at',
];

function csvRowForItem(
  item: CleaningItemScore,
  context: {
    scoreVersion: string | null;
    computedAt: string | null;
    family: CleaningFamily;
    focusSubscores: readonly string[];
  }
): CsvValue[] {
  const familyScore = focusedFamilyScore(
    item,
    familyScoreForItem(item, context.family),
    context.focusSubscores
  );
  const masterLabelUrl = masterLabelUrlForItem(item, context.family, familyScore);
  return [
    item.itemKey,
    item.itemNumber,
    item.normalizedBarcodes.join(' '),
    familyScore.score,
    familyScore.severity,
    familyScore.worstSubscore,
    ...SUBSCORE_META.map((meta) => subscoreValueForItem(item, meta)),
    familyScore.finding?.message ?? null,
    familyScore.finding?.otherItemKey ?? null,
    familyScore.finding?.similarity ?? null,
    item.counts.designs,
    item.counts.activeFronts,
    item.counts.unassigned,
    item.counts.pending,
    item.counts.cartons,
    item.counts.reviewFlagged ?? 0,
    item.reasonCodes.join(' '),
    masterLabelUrl ? `${window.location.origin}${masterLabelUrl}` : null,
    item.chainId,
    context.scoreVersion,
    context.computedAt,
  ];
}

/** Compact Master Label identifier: "in:<item number>" or "nb:<normalized barcode>". */
function itemIdentifierLabel(item: CleaningItemScore): string {
  if (item.itemNumber) return `in:${item.itemNumber}`;
  if (item.normalizedBarcodes.length > 0) return `nb:${item.normalizedBarcodes[0]}`;
  return item.itemKey;
}

function itemKeyLabel(itemKey: string): string {
  return itemKey.startsWith('barcode:')
    ? `nb:${itemKey.slice('barcode:'.length)}`
    : `in:${itemKey}`;
}

const SORT_OPTIONS: readonly CleaningSort[] = [
  'score',
  'class_coherence',
  'class_confusion',
  // Kept so bookmarked old-version views remain valid.
  'front_split',
  'front_strays',
  'front_confusion',
  'duplicate_front_classes',
  'front_fragmentation',
  'nonfront_confusion',
  'carton_confusion',
  'partial_group_front_alignment',
  'nonfront_group_alignment',
  'identity_resolution',
  'assignment',
  'review_flags',
  'metadata_integrity',
];

function chainIdParam(): {
  parse: (values: string[]) => number | null;
  serialize: (value: number | null) => string[];
} {
  return {
    parse: (values) => {
      const parsed = Number.parseInt(values[0] ?? '', 10);
      return Number.isFinite(parsed) ? parsed : null;
    },
    serialize: (value) => (value === null ? [] : [String(value)]),
  };
}

const ITEMS_URL_FILTERS = {
  chain: chainIdParam(),
  family: enumParam(['combined', 'training', 'inference', 'catalog'] as const, 'combined'),
  duplicates: enumParam(['show', 'hide'] as const, 'hide'),
  severity: enumParam(['critical', 'high', 'medium', 'low', 'clean'] as const, 'all'),
  excluded: enumParam(['hide', 'only'] as const, 'hide'),
  issue: stringListParam(),
  scope: enumParam(['one_design', 'full_group'] as const, 'all'),
  locks: enumParam(['any', 'all', 'some', 'none'] as const, 'any'),
  mods: stringListParam(),
  focus: enumParam(['all', 'confusion'] as const, 'all'),
  q: stringParam(),
  sort: enumParam(SORT_OPTIONS, 'score'),
  page: pageParam(),
};

const LOCK_FILTER_OPTIONS: Array<{ value: CleaningLockFilter; label: string }> = [
  { value: 'any', label: 'Any lock state' },
  { value: 'all', label: 'All locked' },
  { value: 'some', label: 'Partially locked' },
  { value: 'none', label: 'None locked' },
];

/** Unresolved mod prefixes travel on the wire as the UNKNOWN bucket. */
const MOD_PREFIX_UNKNOWN = 'UNKNOWN';

// The Confusion focus keeps every item the Confusion check graded (any value,
// clean tail included) or flagged with a red finding, sorted worst-first, and
// reroutes links to the confusion finding wherever one exists.
const CONFUSION_FOCUS_SUBSCORES: string[] = ['class_confusion'];

const CONFUSION_FOCUS_SORT: CleaningSort = 'class_confusion';

function usesRawT1Similarity(scoreVersion?: string | null): boolean {
  if (!scoreVersion) return false;
  const [major, minor] = scoreVersion.split('.').map((part) => Number(part));
  return major === 2 && minor >= 2 && minor < 6;
}

function usesRawFragmentation(scoreVersion?: string | null): boolean {
  if (!scoreVersion) return false;
  const [major, minor] = scoreVersion.split('.').map((part) => Number(part));
  return major === 2 && minor >= 3 && minor < 6;
}

function normalizedSubscoreValue(
  field: SubscoreMetaEntry,
  value: number,
  scoreVersion?: string | null
): number {
  if (
    (field.key === 'frontSplit' && usesRawT1Similarity(scoreVersion)) ||
    (field.key === 'frontFragmentation' && usesRawFragmentation(scoreVersion))
  ) {
    return 100 * Math.max(0, Math.min(1, value));
  }
  return value;
}

function subscoreChipSeverity(
  field: SubscoreMetaEntry,
  value?: number | null,
  scoreVersion?: string | null
): 'red' | 'amber' | 'ok' | 'na' {
  if (value === null || value === undefined) return 'na';
  const normalized = normalizedSubscoreValue(field, value, scoreVersion);
  // Mirrors the backend severity bands (high < 82, medium < 90).
  if (normalized < 82) return 'red';
  if (normalized < 90) return 'amber';
  return 'ok';
}

const SUBSCORE_CHIP_COLORS: Record<'red' | 'amber' | 'ok' | 'na', string> = {
  red: '#e8523f',
  amber: '#eea337',
  ok: 'var(--text-tertiary)',
  na: 'var(--text-tertiary)',
};

function formatNumber(value: number): string {
  return value.toLocaleString();
}

function formatScore(value?: number | null): string {
  if (value === null || value === undefined) return '-';
  return Math.round(value).toString();
}

function formatMeanScore(value?: number | null): string {
  if (value === null || value === undefined) return '-';
  return value.toFixed(1);
}

function formatSubscoreValue(
  field: SubscoreMetaEntry,
  value?: number | null,
  scoreVersion?: string | null
): string {
  if (value === null || value === undefined) return '-';
  return formatScore(normalizedSubscoreValue(field, value, scoreVersion));
}

function itemStatusBadge(item: CleaningItemScore): React.ReactNode {
  if (item.excluded) {
    return <span className="dg-scope-pill is-unreviewed">No changes needed</span>;
  }

  if (item.exclusionOutdated) {
    return (
      <span
        className="dg-scope-pill is-partial"
        title="The previous No changes needed mark predates a scoring or evidence change. Refreshing does not clear the mark — review and mark it again."
      >
        Mark outdated
      </span>
    );
  }

  if (item.previousSeverity && item.previousSeverity !== 'clean' && item.severity === 'clean') {
    return (
      <span
        className="dg-scope-pill is-reviewed"
        title={`Was ${item.previousSeverity} last refresh — now clean`}
      >
        Resolved
      </span>
    );
  }

  return null;
}

function lockStatusBadge(item: CleaningItemScore): React.ReactNode {
  // Absent (older API) or no locked designs: no badge — absence means "nothing locked".
  if (item.lockStatus !== 'all' && item.lockStatus !== 'some') return null;
  // Count distinct, truthy design ids exactly as the backend rollup does, or a
  // duplicate/id-less design score would show "2/3" next to a fully-locked badge.
  const total = new Set(item.designScores.map((score) => score.designId).filter(Boolean)).size;
  const locked = item.lockedDesignCount ?? 0;
  const isFull = item.lockStatus === 'all';
  return (
    <span
      className={`dg-scope-pill ${isFull ? 'is-full' : 'is-partial'}`}
      data-testid="cleaning-lock-badge"
      data-lock-status={item.lockStatus}
      title={
        isFull
          ? `All ${total} of this Item number's designs are locked for training`
          : `${locked} of ${total} of this Item number's designs are locked for training`
      }
    >
      <LockOutlinedIcon style={{ fontSize: 11, verticalAlign: 'text-bottom' }} /> {locked}/{total}
    </span>
  );
}

function modPrefixChip(item: CleaningItemScore): React.ReactNode {
  if (!item.modPrefix) return null;
  return (
    <span
      data-testid="cleaning-mod-prefix"
      style={{
        fontFamily: 'var(--font-mono)',
        fontSize: 10,
        fontWeight: 700,
        padding: '1px 5px',
        borderRadius: 4,
        border: '1px solid var(--border-color, rgba(128,128,128,0.4))',
        color: 'var(--text-secondary)',
      }}
      title={`Mod prefix: ${item.modPrefix}`}
    >
      {item.modPrefix}
    </span>
  );
}

function trainingTaskTypeSummary(finding?: CleaningFinding | null): React.ReactNode {
  if (!finding?.issueType) return null;

  const summary = trainingIssueMetadata(finding.issueType);
  if (!summary) return null;
  const scope = finding.issueScope ? TRAINING_ISSUE_SCOPE_LABELS[finding.issueScope] : null;
  const description = `${summary.code} · ${summary.label}${scope ? ` · ${scope}` : ''}`;

  return (
    <span
      className={`cleaning-training-task-summary is-${finding.issueType.replaceAll('_', '-')}`}
      data-testid="training-task-type"
      data-issue-type={finding.issueType}
      title={`Task type: ${description}`}
      aria-label={`Task type: ${description}`}
    >
      <span className="cleaning-training-task-code">{summary.code}</span>
      <span className="cleaning-training-task-label">{summary.label}</span>
      {scope ? <span className="cleaning-training-task-scope">· {scope}</span> : null}
    </span>
  );
}

function findingVerdict(finding: CleaningFinding, fallbackSubscore?: string): string {
  const issueMetadata = trainingIssueMetadata(finding.issueType);
  if (issueMetadata) {
    return finding.issueScope === 'full_group' && issueMetadata.fullGroupMessage
      ? issueMetadata.fullGroupMessage
      : issueMetadata.message;
  }

  if (finding.diagnosis === 'split') {
    return 'Blue and purple Crops look different.';
  }
  if (finding.diagnosis === 'strays') {
    return 'Orange Crops do not match the others.';
  }
  if (finding.diagnosis === 'fragmentation') {
    return 'Orange Crops do not match this Design.';
  }

  switch (finding.subscore || fallbackSubscore) {
    case 'class_coherence':
      return 'This Training class may contain Crops that do not belong together.';
    case 'class_confusion':
    case 'front_confusion':
    case 'duplicate_front_classes':
      return 'This Training class looks too similar to another Training class.';
    case 'nonfront_confusion':
      return 'These non-front Crops look too similar to Crops from another item.';
    case 'carton_confusion':
      return 'This carton looks too similar to another item’s carton.';
    case 'partial_group_front_alignment':
      return 'This partial Design group may connect fronts that are not similar enough.';
    case 'nonfront_group_alignment':
      return 'This non-front Design group may connect views that are not similar enough.';
    case 'identity_resolution':
    case 'identity':
      return 'A normalized barcode does not resolve cleanly to one Item number.';
    case 'assignment':
      return 'Some Crops still need a Design assignment.';
    case 'review_flags':
    case 'review_load':
      return 'Known Crop issues are still waiting for review.';
    case 'metadata_integrity':
      return 'Some Crop metadata is contradictory.';
    default:
      return 'This item has evidence that needs review.';
  }
}

function scoreDeltaIndicator(item: CleaningItemScore): React.ReactNode {
  if (item.previousScore === null || item.previousScore === undefined) return null;

  const delta = item.score - item.previousScore;
  if (Math.abs(delta) < 0.5) return null;

  const isImproved = delta > 0;
  return (
    <span
      style={{
        display: 'inline-block',
        width: 8,
        marginLeft: 4,
        color: isImproved ? '#72c77a' : '#ee7565',
        fontSize: 10,
        lineHeight: 1,
        verticalAlign: 'middle',
      }}
      title={`${formatScore(item.previousScore)} → ${formatScore(item.score)} since last refresh`}
    >
      {isImproved ? '▲' : '▼'}
    </span>
  );
}

function uniqueCropIds(cropIds?: string[]): string[] {
  return [...new Set((cropIds ?? []).map((cropId) => cropId.trim()).filter(Boolean))];
}

function masterLabelHighlightCounts(
  item: CleaningItemScore,
  family: CleaningFamily,
  score: CleaningFamilyScore = familyScoreForItem(item, family)
): {
  primary: number;
  secondary: number;
  tertiary: number;
} {
  const params = score.masterLabelParams;
  const primaryCropIds = uniqueCropIds(params?.cropIds);
  const primaryCropIdSet = new Set(primaryCropIds);
  const secondaryCropIds = uniqueCropIds(params?.secondaryCropIds).filter(
    (cropId) => !primaryCropIdSet.has(cropId)
  );
  const usedCropIdSet = new Set([...primaryCropIds, ...secondaryCropIds]);
  const tertiaryCropIds = uniqueCropIds(params?.tertiaryCropIds).filter(
    (cropId) => !usedCropIdSet.has(cropId)
  );
  return {
    primary: primaryCropIds.length,
    secondary: secondaryCropIds.length,
    tertiary: tertiaryCropIds.length,
  };
}

function masterLabelHighlightCountSummary(
  item: CleaningItemScore,
  family: CleaningFamily,
  score: CleaningFamilyScore = familyScoreForItem(item, family)
): string | null {
  const params = score.masterLabelParams;
  const { primary, secondary, tertiary } = masterLabelHighlightCounts(item, family, score);
  if (primary === 0 && secondary === 0 && tertiary === 0) return null;
  if (params?.highlightMode === 'coherence_confusion') {
    return `${formatNumber(secondary)} suspicious + ${formatNumber(primary)} current-class + ${formatNumber(tertiary)} matching-class crops highlighted`;
  }
  if (params?.highlightMode === 'peer_groups' && primary > 0 && secondary > 0) {
    const total = primary + secondary;
    return `${formatNumber(total)} ${total === 1 ? 'crop' : 'crops'} highlighted in two peer groups (${primary} + ${secondary})`;
  }
  const referenceSummary = secondary > 0 ? ` (+${formatNumber(secondary)} reference)` : '';
  return `${formatNumber(primary)} ${primary === 1 ? 'crop' : 'crops'} highlighted${referenceSummary}`;
}

function masterLabelOpenSummary(
  item: CleaningItemScore,
  family: CleaningFamily,
  score: CleaningFamilyScore = familyScoreForItem(item, family)
): string {
  const highlightSummary = masterLabelHighlightCountSummary(item, family, score);
  return highlightSummary ? `opens Master Label with ${highlightSummary}` : 'opens Master Label';
}

export function masterLabelLinkDescription(
  item: CleaningItemScore,
  family: CleaningFamily = 'training',
  score: CleaningFamilyScore = familyScoreForItem(item, family)
): string {
  const message = score.finding?.message?.trim();
  if (!message) return 'Open in Master Label';
  return `${message} — ${masterLabelOpenSummary(item, family, score)}`;
}

function masterLabelDetailsHighlightSummary(
  item: CleaningItemScore,
  family: CleaningFamily,
  score: CleaningFamilyScore = familyScoreForItem(item, family)
): string | null {
  const params = score.masterLabelParams;
  if (!params) return null;
  const { primary, secondary, tertiary } = masterLabelHighlightCounts(item, family, score);
  if (primary === 0 && secondary === 0 && tertiary === 0) {
    return 'Link opens Master Label without crop highlights';
  }
  if (params.highlightMode === 'coherence_confusion') {
    return `Link highlights current-class evidence (${formatNumber(primary)}), suspicious Crops (${formatNumber(secondary)}), and the matching Training class (${formatNumber(tertiary)})`;
  }
  if (params.highlightMode === 'peer_groups' && primary > 0 && secondary > 0) {
    return `Link highlights two peer groups (${formatNumber(primary)} + ${formatNumber(secondary)} crops)`;
  }
  const referenceSummary = secondary > 0 ? ` (+${formatNumber(secondary)} reference)` : '';
  return `Link highlights ${formatNumber(primary)} ${
    primary === 1 ? 'crop' : 'crops'
  }${referenceSummary}`;
}

function cleaningCountRows(item: CleaningItemScore): Array<[string, number]> {
  const rows: Array<[string, number]> = [
    ['designs', item.counts.designs],
    ['active fronts', item.counts.activeFronts],
    ['unassigned', item.counts.unassigned],
    ['pending', item.counts.pending],
    ['cartons', item.counts.cartons],
  ];
  if ((item.counts.reviewFlagged ?? 0) > 0) {
    rows.push(['Review flagged', item.counts.reviewFlagged ?? 0]);
  }
  if ((item.counts.metadataIssues ?? 0) > 0) {
    rows.push(['Metadata issues', item.counts.metadataIssues ?? 0]);
  }
  return rows;
}

const headerCellStyle: React.CSSProperties = {
  padding: '8px 10px',
  textAlign: 'left',
  fontSize: 10,
  color: 'var(--text-tertiary)',
  fontWeight: 800,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  borderBottom: '1px solid var(--divider)',
  verticalAlign: 'middle',
};

const familyScoreHeaderCellStyle: React.CSSProperties = {
  ...headerCellStyle,
  width: 82,
  minWidth: 82,
  maxWidth: 92,
  padding: '8px 4px',
  textAlign: 'center',
};

const sortableFamilyScoreHeaderCellStyle: React.CSSProperties = {
  ...familyScoreHeaderCellStyle,
  cursor: 'pointer',
  userSelect: 'none',
};

// Fixed cell width keeps header index cells and row value cells pixel-aligned,
// and keeps the strip compact enough to scale past 20 subscores (groups wrap
// as a unit when the row runs out of width).
const SUBSCORE_CELL_WIDTH = 56;

const subscoreSortHeaderStyle: React.CSSProperties = {
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: SUBSCORE_CELL_WIDTH,
  fontSize: 10,
  fontWeight: 800,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  cursor: 'pointer',
  userSelect: 'none',
  whiteSpace: 'nowrap',
};

const subscoreGroupCaptionBaseStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 4,
  fontSize: 9,
  fontWeight: 850,
  // Tight tracking so a single-cell group caption ("SIGNAL") fits its 38px
  // cell without clipping.
  letterSpacing: '0.01em',
  textTransform: 'uppercase',
  color: 'var(--text-tertiary)',
  whiteSpace: 'nowrap',
};

// width:0 + minWidth:100% keeps a long caption from widening its group beyond
// the strip cells, so header and row groups stay aligned.
const subscoreGroupCaptionStyle: React.CSSProperties = {
  ...subscoreGroupCaptionBaseStyle,
  width: 0,
  minWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const subscoreGroupDividerStyle: React.CSSProperties = {
  alignSelf: 'stretch',
  width: 1,
  flexShrink: 0,
  background: 'var(--divider)',
};

function subscoreGroupDotStyle(color: string): React.CSSProperties {
  return { flexShrink: 0, width: 6, height: 6, borderRadius: 2, background: color };
}

const subscoreValueCellStyle: React.CSSProperties = {
  display: 'inline-flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: SUBSCORE_CELL_WIDTH,
  padding: '2px 0',
  borderRadius: 4,
  fontFamily: 'var(--font-mono)',
};

const SUBSCORE_CHIP_BACKGROUNDS: Record<'red' | 'amber' | 'ok' | 'na', string> = {
  red: 'color-mix(in srgb, #e8523f 14%, transparent)',
  amber: 'color-mix(in srgb, #eea337 14%, transparent)',
  ok: 'transparent',
  na: 'transparent',
};

const bodyCellStyle: React.CSSProperties = {
  padding: '9px 10px',
  borderBottom: '1px solid var(--divider)',
  color: 'var(--text-secondary)',
  fontSize: 12,
  verticalAlign: 'middle',
};

const monoCellStyle: React.CSSProperties = {
  ...bodyCellStyle,
  fontFamily: 'var(--font-mono)',
};

const refreshButtonStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  border: '1px solid var(--border)',
  background: 'var(--surface)',
  color: 'var(--text-secondary)',
  borderRadius: 6,
  height: 28,
  padding: '0 10px',
  fontSize: 11,
  fontWeight: 700,
  cursor: 'pointer',
};

const tabButtonStyle: React.CSSProperties = {
  border: '1px solid var(--border)',
  background: 'var(--surface)',
  color: 'var(--text-secondary)',
  borderRadius: 6,
  height: 26,
  padding: '0 10px',
  fontSize: 11,
  fontWeight: 700,
  cursor: 'pointer',
};

const tabButtonActiveStyle: React.CSSProperties = {
  ...tabButtonStyle,
  background: 'var(--accent-soft, var(--elevated))',
  color: 'var(--text-primary)',
  border: '1px solid var(--text-tertiary)',
};

const selectStyle: React.CSSProperties = {
  border: '1px solid var(--border)',
  background: 'var(--surface)',
  color: 'var(--text-primary)',
  borderRadius: 6,
  height: 28,
  padding: '0 8px',
  fontSize: 11,
  fontWeight: 700,
};

const pageButtonStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid var(--border)',
  background: 'var(--surface)',
  color: 'var(--text-secondary)',
  borderRadius: 6,
  width: 26,
  height: 26,
  cursor: 'pointer',
};

const evidenceThumbStyle: React.CSSProperties = {
  width: 46,
  height: 46,
  objectFit: 'cover',
  borderRadius: 4,
  border: '1px solid var(--border)',
};

interface ActiveCleaningRun {
  chainId: number;
  runId: string;
}

function TableBusyOverlay({ label, testId }: { label: string; testId: string }) {
  return (
    <div className="forge-busy-overlay" role="status" aria-live="polite">
      <span className="forge-busy-overlay-badge" data-testid={testId}>
        {label}
      </span>
    </div>
  );
}

/** SVG arc for one donut slice (angles in radians; 0 = 12 o'clock, clockwise). */
function donutArcPath(cx: number, cy: number, r: number, start: number, end: number): string {
  const angleToPoint = (angle: number) => ({
    x: cx + r * Math.sin(angle),
    y: cy - r * Math.cos(angle),
  });
  const from = angleToPoint(start);
  const to = angleToPoint(end);
  const largeArc = end - start > Math.PI ? 1 : 0;
  return `M ${from.x.toFixed(3)} ${from.y.toFixed(3)} A ${r} ${r} 0 ${largeArc} 1 ${to.x.toFixed(3)} ${to.y.toFixed(3)}`;
}

function ChainStatsBand({
  stats,
  history,
  scoreSchemaVersion,
  currentScoreVersion,
  family,
  activeSort,
  onSelectSort,
}: {
  stats: CleaningChainStatsResponse;
  history: CleaningChainHistoryResponse | null;
  scoreSchemaVersion: number | null;
  currentScoreVersion: string | null;
  family: CleaningFamily;
  activeSort: CleaningSort;
  onSelectSort: (sort: CleaningSort) => void;
}) {
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const isOldScoreVersion =
    stats.scoreVersion != null &&
    currentScoreVersion != null &&
    stats.scoreVersion !== currentScoreVersion;
  const versionMeta = subscoreMetaForVersion(
    scoreSchemaVersion,
    stats.scoreVersion,
    currentScoreVersion
  );
  const versionMetaByGroup = subscoreMetaByGroupForVersion(
    scoreSchemaVersion,
    stats.scoreVersion,
    currentScoreVersion
  );
  const selectedMeta = family === 'combined' ? versionMeta : versionMetaByGroup[family];
  const versionWeightByGroup = SUBSCORE_GROUPS.reduce(
    (acc, group) => {
      acc[group.key] = versionMetaByGroup[group.key].reduce((sum, entry) => sum + entry.weight, 0);
      return acc;
    },
    {} as Record<SubscoreGroupKey, number>
  );
  const versionTotalWeight = Object.values(versionWeightByGroup).reduce(
    (sum, weight) => sum + weight,
    0
  );
  const entries = selectedMeta.flatMap((meta) => {
    const legacyKey = LEGACY_STATS_KEYS[meta.sort];
    const stat = stats.subscores[meta.sort] ?? (legacyKey ? stats.subscores[legacyKey] : undefined);
    if (!stat) return [];
    const impactScale =
      family === 'combined' ? versionWeightByGroup[meta.group] / versionTotalWeight : 1;
    return [{ meta, stat: { ...stat, impact: stat.impact * impactScale } }];
  });
  if (family === 'training' && stats.subscores.h1_cohesion) {
    entries.push({ meta: LEGACY_COHESION_STATS_META, stat: stats.subscores.h1_cohesion });
  }
  const totalGap = entries.reduce((acc, entry) => acc + entry.stat.impact, 0);
  // Canonical check order, not impact order, so the donut reads the
  // same way on every chain.
  const contributors = entries.filter((entry) => entry.stat.impact > 0.05);
  const negligibleImpactCount = entries.length - contributors.length;
  const familyMeanScore =
    family === 'combined' ? stats.meanScore : (stats.familyMeanScores?.[family] ?? stats.meanScore);
  const trendPoints = (history?.points ?? []).map((point) => ({
    timestamp: point.completedAt,
    value:
      family === 'combined'
        ? (point.meanScore ?? null)
        : (point.familyMeanScores?.[family] ?? point.meanScore ?? null),
    versions: { scoring: point.scoreVersion, model: point.embeddingModelVersion },
    detail: [
      `${formatNumber(point.itemCount)} items`,
      ...(point.durationSeconds != null
        ? [`refresh took ${formatDuration(point.durationSeconds * 1000)}`]
        : []),
    ],
  }));

  return (
    <div
      data-testid="cleaning-chain-stats"
      style={{
        display: 'grid',
        gap: 8,
        padding: '10px 12px',
        border: '1px solid var(--border)',
        borderRadius: 8,
        background: 'var(--surface)',
        opacity: isOldScoreVersion ? 0.72 : 1,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          gap: 10,
          flexWrap: 'wrap',
        }}
      >
        <span style={subscoreGroupCaptionBaseStyle}>
          Where {getForgeChainLabel(stats.chainId)} loses points
          {isOldScoreVersion ? ` · ${stats.scoreVersion} old version` : ''}
        </span>
        <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
          mean{' '}
          <span style={{ color: meanScoreColor(familyMeanScore), fontWeight: 800 }}>
            {formatScore(familyMeanScore)}
          </span>{' '}
          · gap -{totalGap.toFixed(1)} pts · {formatNumber(stats.itemCount)} items
        </span>
      </div>
      <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
        Each slice = points of the chain&apos;s mean score lost to that check. Click a slice to sort
        the item list.
      </span>
      <div style={{ display: 'flex', gap: 18, alignItems: 'center', flexWrap: 'wrap' }}>
        {totalGap > 0.05 ? (
          <>
            <svg
              viewBox="0 0 120 120"
              width={124}
              height={124}
              role="img"
              aria-label={`Score gap by check: ${contributors
                .map((entry) => `${entry.meta.full} ${entry.stat.impact.toFixed(1)} points`)
                .join(', ')}`}
              style={{ flexShrink: 0 }}
            >
              <circle
                cx={60}
                cy={60}
                r={46}
                fill="none"
                stroke="var(--elevated)"
                strokeWidth={17}
              />
              {(() => {
                const gapAngle = contributors.length > 1 ? 0.03 : 0;
                let cursor = 0;
                return contributors.map(({ meta, stat }) => {
                  const fraction = stat.impact / totalGap;
                  const start = cursor * Math.PI * 2 + gapAngle / 2;
                  cursor += fraction;
                  const end = Math.max(start + 0.005, cursor * Math.PI * 2 - gapAngle / 2);
                  const isHovered = hoveredKey === meta.key;
                  const isActiveSort = activeSort === meta.sort;
                  const mid = (start + end) / 2;
                  return (
                    <React.Fragment key={meta.key}>
                      <path
                        data-testid={`chain-stats-segment-${meta.key}`}
                        d={donutArcPath(60, 60, 46, start, end)}
                        fill="none"
                        stroke={meta.color}
                        strokeWidth={isHovered || isActiveSort ? 21 : 17}
                        opacity={hoveredKey !== null && !isHovered ? 0.45 : 1}
                        style={{
                          cursor: 'pointer',
                          transition: 'stroke-width 90ms ease, opacity 90ms ease',
                        }}
                        onMouseEnter={() => setHoveredKey(meta.key)}
                        onMouseLeave={() => setHoveredKey(null)}
                        onClick={() => onSelectSort(meta.sort)}
                      >
                        <title>
                          {`${meta.full}: −${stat.impact.toFixed(1)} pts of mean (${Math.round(
                            (stat.impact / totalGap) * 100
                          )}% of gap) — click to sort items`}
                        </title>
                      </path>
                      {fraction > 0.06 ? (
                        <text
                          data-testid={`chain-stats-slice-label-${meta.key}`}
                          x={60 + 46 * Math.sin(mid)}
                          y={60 - 46 * Math.cos(mid)}
                          textAnchor="middle"
                          dominantBaseline="central"
                          fill="#fff"
                          fontSize={8.5}
                          fontWeight={800}
                          style={{ pointerEvents: 'none' }}
                        >
                          {subscoreIndexLabel(meta)}
                        </text>
                      ) : null}
                    </React.Fragment>
                  );
                });
              })()}
              <text
                x={60}
                y={56}
                textAnchor="middle"
                fill={meanScoreColor(familyMeanScore)}
                fontSize={19}
                fontWeight={800}
                fontFamily="var(--font-mono)"
              >
                {formatScore(familyMeanScore)}
              </text>
              <text x={60} y={70} textAnchor="middle" fill="var(--text-tertiary)" fontSize={8.5}>
                MEAN SCORE
              </text>
              <text
                x={60}
                y={81}
                textAnchor="middle"
                fill="var(--text-secondary)"
                fontSize={9.5}
                fontFamily="var(--font-mono)"
              >
                -{totalGap.toFixed(1)} pts
              </text>
            </svg>
            <div
              style={{ display: 'grid', gap: 3, minWidth: 230, maxWidth: 500, flex: '1 1 230px' }}
            >
              {contributors.map(({ meta, stat }) => {
                const isHovered = hoveredKey === meta.key;
                const isActiveSort = activeSort === meta.sort;
                const sharePercent = Math.round((stat.impact / totalGap) * 100);
                return (
                  <button
                    key={meta.key}
                    type="button"
                    aria-label={`Sort items by ${meta.full}`}
                    title={`Click to sort items by ${meta.full} (dirtiest first)`}
                    onMouseEnter={() => setHoveredKey(meta.key)}
                    onMouseLeave={() => setHoveredKey(null)}
                    onClick={() => onSelectSort(meta.sort)}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'auto minmax(0, 1fr)',
                      alignItems: 'start',
                      columnGap: 6,
                      rowGap: 1,
                      padding: '2px 6px',
                      border: 'none',
                      borderRadius: 4,
                      background: isHovered
                        ? 'color-mix(in srgb, var(--text-primary) 7%, transparent)'
                        : 'transparent',
                      color: 'var(--text-secondary)',
                      fontSize: 11,
                      cursor: 'pointer',
                      textAlign: 'left',
                    }}
                  >
                    <span style={{ ...subscoreGroupDotStyle(meta.color), marginTop: 5 }} />
                    <span
                      style={{
                        color: isActiveSort ? 'var(--text-primary)' : undefined,
                        fontWeight: isActiveSort ? 800 : 500,
                      }}
                    >
                      {subscoreIndexLabel(meta)} · {meta.full}
                      {isActiveSort ? ' ↑' : ''}
                    </span>
                    <span
                      style={{
                        gridColumn: '2',
                        color: 'var(--text-tertiary)',
                        fontSize: 10.5,
                        lineHeight: 1.3,
                      }}
                    >
                      <span
                        style={{
                          fontFamily: 'var(--font-mono)',
                          fontWeight: 800,
                          color: 'var(--text-primary)',
                        }}
                      >
                        −{stat.impact.toFixed(1)} pts of mean
                      </span>{' '}
                      ({sharePercent}% of gap)
                      {stat.worstCount > 0 ? (
                        <> · worst check for {formatNumber(stat.worstCount)} items</>
                      ) : null}
                    </span>
                  </button>
                );
              })}
              {negligibleImpactCount > 0 ? (
                <span style={{ fontSize: 11, color: 'var(--text-tertiary)', padding: '0 6px' }}>
                  +{negligibleImpactCount} checks with negligible impact
                </span>
              ) : null}
            </div>
          </>
        ) : (
          <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
            No measurable score gap — this chain is clean.
          </span>
        )}
        {trendPoints.length >= 2 ? (
          <div
            data-testid="cleaning-chain-trend"
            style={{
              display: 'grid',
              gap: 4,
              flex: '2 1 300px',
              minWidth: 260,
              alignContent: 'start',
            }}
          >
            <span style={subscoreGroupCaptionBaseStyle}>
              Mean score trend
              <span style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 500 }}>
                — dashed: older scoring/model version
              </span>
            </span>
            <VersionedTrendChart
              points={trendPoints}
              ariaLabel={`Mean cleanliness score trend for ${getForgeChainLabel(stats.chainId)}`}
              formatValue={(value) => value.toFixed(0)}
              formatTimestamp={formatTimestamp}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}

const explainerFormulaStyle: React.CSSProperties = {
  margin: '10px 0 0',
  padding: '10px 12px',
  border: '1px solid var(--border)',
  borderRadius: 6,
  background: 'var(--elevated)',
  color: 'var(--text-primary)',
  fontFamily: 'var(--font-mono)',
  fontSize: 11,
  lineHeight: 1.6,
  overflowX: 'auto',
  whiteSpace: 'pre',
};

const explainerLabelStyle: React.CSSProperties = {
  marginTop: 12,
  color: 'var(--text-tertiary)',
  fontSize: 10,
  fontWeight: 850,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
};

const explainerBodyTextStyle: React.CSSProperties = {
  margin: '4px 0 0',
  color: 'var(--text-secondary)',
  fontSize: 12,
  lineHeight: 1.55,
};

type ExplainerTabKey = 'overview' | CleaningSort;

// Worst-check tie order, mirrored from the backend's _WORST_SUBSCORE_PRIORITY.
const WORST_TIE_PRIORITY_LABEL =
  'Coherence › Confusion › Non-front › Carton › Partial group › Non-front group › Metadata › Assignment › Resolution › Review';

function ScoreExplainerDialog({
  onClose,
  scoreVersion,
}: {
  onClose: () => void;
  scoreVersion?: string | null;
}) {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const tabRefs = useRef<Map<ExplainerTabKey, HTMLButtonElement>>(new Map());
  const [activeTab, setActiveTab] = useState<ExplainerTabKey>('overview');

  useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const tabKeys: ExplainerTabKey[] = ['overview', ...SUBSCORE_META.map((entry) => entry.sort)];

  const selectTab = (key: ExplainerTabKey) => {
    setActiveTab(key);
    tabRefs.current.get(key)?.focus();
  };

  const handleTabListKeyDown = (event: React.KeyboardEvent) => {
    const currentIndex = tabKeys.indexOf(activeTab);
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      selectTab(tabKeys[(currentIndex + 1) % tabKeys.length]);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      selectTab(tabKeys[(currentIndex - 1 + tabKeys.length) % tabKeys.length]);
    } else if (event.key === 'Home') {
      event.preventDefault();
      selectTab(tabKeys[0]);
    } else if (event.key === 'End') {
      event.preventDefault();
      selectTab(tabKeys[tabKeys.length - 1]);
    }
  };

  const activeEntry =
    activeTab === 'overview' ? null : SUBSCORE_META.find((entry) => entry.sort === activeTab);

  return (
    <div
      className="mdc-modal-backdrop"
      data-testid="cleaning-score-explainer-dialog"
      onClick={onClose}
    >
      <div
        className="mdc-modal cleaning-score-explainer-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cleaning-score-explainer-title"
        tabIndex={-1}
        ref={dialogRef}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mdc-modal-header">
          <div>
            <h2 id="cleaning-score-explainer-title">How cleanliness scoring works</h2>
          </div>
          <button type="button" className="mdc-modal-close" aria-label="Close" onClick={onClose}>
            <CloseOutlinedIcon style={{ fontSize: 18 }} />
          </button>
        </div>
        <div className="mdc-modal-body" style={{ padding: '12px 18px 18px', gap: 0 }}>
          <div
            role="tablist"
            aria-label="Scoring explainer sections"
            onKeyDown={handleTabListKeyDown}
            style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}
          >
            {tabKeys.map((key) => {
              const entry =
                key === 'overview' ? null : SUBSCORE_META.find((meta) => meta.sort === key);
              const isActive = activeTab === key;
              return (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  tabIndex={isActive ? 0 : -1}
                  ref={(node) => {
                    if (node) tabRefs.current.set(key, node);
                    else tabRefs.current.delete(key);
                  }}
                  style={isActive ? tabButtonActiveStyle : tabButtonStyle}
                  onClick={() => setActiveTab(key)}
                >
                  {entry ? entry.short : 'Overview'}
                </button>
              );
            })}
          </div>
          {!activeEntry ? (
            <div role="tabpanel" aria-label="Overview">
              <p style={{ ...explainerBodyTextStyle, margin: 0, fontSize: 13 }}>
                Every item has Training, Inference, and Catalog family scores, plus one weighted
                overall cleanliness score. The selected family controls the task ranking, severity
                filter, task link, and visible subscores. Family scores stay independent, while all
                three contribute to overall cleanliness. A check that did not apply
                (&ldquo;–&rdquo;) counts as clean.
              </p>
              <p style={explainerBodyTextStyle}>
                Every current check uses 0–100 cleanliness points. The table rounds values to whole
                points for scanning, while sorting and exports retain the precise score. Scores
                below 10 are in the soft-floor tail: the check failed badly, and lower still means
                worse — they stay sortable instead of tying at the floor.
              </p>
              <div style={explainerLabelStyle}>Composition</div>
              <pre style={explainerFormulaStyle}>
                {'Training class = mean(Coherence, Confusion)\n' +
                  'Training item = minimum Training-class score for that Item number\n' +
                  'Inference / Catalog = weighted mean of their item checks\n' +
                  'overall = weighted mean(Training item, Inference checks, Catalog checks)\n' +
                  'checks that could not run count as clean (100)\n' +
                  '0–100 checks: soft_floor(raw) = raw if raw >= 10, else 10 * exp((raw - 10) / 25)\n' +
                  'overall weights: Training counts as two 1.0 axes; Inference 0.5 each; Catalog 0.05 each\n' +
                  'Training is the default because Training classes define recognition classes'}
              </pre>
              <div style={explainerLabelStyle}>Checks by family</div>
              {SUBSCORE_GROUPS.map((group) => (
                <div key={group.key} style={{ marginTop: 6 }}>
                  <div
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      color: 'var(--text-primary)',
                      fontSize: 12,
                      fontWeight: 800,
                    }}
                  >
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: 2,
                        background: group.color,
                      }}
                    />
                    {group.label}
                  </div>
                  <span style={{ color: 'var(--text-tertiary)', fontSize: 11, marginLeft: 8 }}>
                    {group.description} —{' '}
                    {SUBSCORE_META.filter((entry) => entry.group === group.key)
                      .map((entry) => entry.short)
                      .join(', ')}
                  </span>
                </div>
              ))}
              <div style={explainerLabelStyle}>Worst check</div>
              <p style={explainerBodyTextStyle}>
                For overall cleanliness, the worst check is the one causing the largest weighted
                loss from 100. Inside a family, it is the lowest normalized cleanliness value. It
                drives the suggested action, finding, and Master Label link. The Training score
                itself still comes from the lowest real class mean; the two sortable item columns
                retain the lowest Coherence and Confusion seen anywhere under that Item number. Ties
                break in this order: {WORST_TIE_PRIORITY_LABEL}.
              </p>
              <div style={explainerLabelStyle}>Severity buckets</div>
              <pre style={explainerFormulaStyle}>
                {'critical < 75 <= high < 82 <= medium < 90 <= low < 97 <= clean'}
              </pre>
              <p style={{ ...explainerBodyTextStyle, color: 'var(--text-tertiary)' }}>
                A chain&apos;s mean score is the plain average of its items&apos; scores. The chain
                stats bar splits each family&apos;s gap to 100 by check. For Training, the
                minimum-class gap is attributed proportionally to the two displayed axis losses.
              </p>
            </div>
          ) : (
            <div role="tabpanel" aria-label={activeEntry.full}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  color: 'var(--text-primary)',
                  fontWeight: 800,
                  fontSize: 14,
                }}
              >
                {activeEntry.full}
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                    padding: '1px 8px',
                    borderRadius: 999,
                    border: '1px solid var(--border)',
                    color: 'var(--text-secondary)',
                    fontSize: 10,
                    fontWeight: 800,
                  }}
                >
                  <span
                    style={{
                      width: 7,
                      height: 7,
                      borderRadius: 2,
                      background: SUBSCORE_GROUPS_BY_KEY[activeEntry.group].color,
                    }}
                  />
                  {SUBSCORE_GROUPS_BY_KEY[activeEntry.group].label}
                </span>
                <span style={{ color: 'var(--text-tertiary)', fontSize: 11, fontWeight: 700 }}>
                  weight *{activeEntry.weight}
                </span>
              </div>
              <p style={{ ...explainerBodyTextStyle, fontSize: 13 }}>{activeEntry.meaning}</p>
              <div style={explainerLabelStyle}>
                Formula (SCORE VERSION {scoreVersion ?? 'CURRENT'})
              </div>
              <pre style={explainerFormulaStyle}>{activeEntry.formula.join('\n')}</pre>
              {activeEntry.example ? (
                <>
                  <div style={explainerLabelStyle}>Worked example</div>
                  <p style={explainerBodyTextStyle}>{activeEntry.example}</p>
                </>
              ) : null}
              <div style={explainerLabelStyle}>Master Label highlight</div>
              <p style={explainerBodyTextStyle}>{activeEntry.highlights}</p>
              <div style={explainerLabelStyle}>Fix</div>
              <p style={explainerBodyTextStyle}>{activeEntry.fix}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export const CleaningTasksDashboard: React.FC = () => {
  const { token, user } = useAuthContext();
  const cacheScope = user?.id ?? null;
  const { showErrorToast, showSuccessToast } = useNotificationContext();
  const [dashboard, setDashboard] = useState<CleaningDashboardResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // True only while a refetch runs after the first successful dashboard load, so the
  // chains table keeps showing its previous rows instead of unmounting into a loading row.
  const [isDashboardUpdating, setIsDashboardUpdating] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [refreshingChainIds, setRefreshingChainIds] = useState<Set<number>>(() => new Set());
  const [activeRuns, setActiveRuns] = useState<ActiveCleaningRun[]>([]);
  const pollErrorRunIdsRef = useRef<Set<string>>(new Set());
  const pollFailureCountsRef = useRef<Map<string, number>>(new Map());
  const hasLoadedDashboardOnceRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const loadDashboard = async () => {
      const requestKey = 'dashboard';
      let servedCachedDashboard = false;
      if (reloadKey === 0 && !hasLoadedDashboardOnceRef.current) {
        const cachedDashboard = readCleaningCacheSlot<CleaningDashboardResponse>(
          cacheScope,
          CLEANING_CACHE_SLOTS.dashboard,
          requestKey
        );
        if (cachedDashboard) {
          servedCachedDashboard = true;
          setDashboard(cachedDashboard);
          hasLoadedDashboardOnceRef.current = true;
          setIsLoading(false);
          setIsDashboardUpdating(true);
        }
      }
      if (hasLoadedDashboardOnceRef.current) {
        setIsDashboardUpdating(true);
      } else {
        setIsLoading(true);
      }
      try {
        const response = await ForgeApiService.fetchCleaningDashboard(token);
        if (!cancelled) {
          setLoadError(null);
          setDashboard(response);
          const resumedRuns = (response.activeRuns ?? [])
            .filter(
              (run) => run.status === 'running' && run.chainId !== null && run.chainId !== undefined
            )
            .map((run) => ({ chainId: run.chainId as number, runId: run.runId }));
          if (resumedRuns.length > 0) {
            setActiveRuns((current) => {
              const byRunId = new Map(current.map((run) => [run.runId, run]));
              resumedRuns.forEach((run) => byRunId.set(run.runId, run));
              return [...byRunId.values()];
            });
            setRefreshingChainIds(
              (current) => new Set([...current, ...resumedRuns.map((run) => run.chainId)])
            );
          }
          hasLoadedDashboardOnceRef.current = true;
          writeCleaningCacheSlot(cacheScope, CLEANING_CACHE_SLOTS.dashboard, requestKey, response);
        }
      } catch (error) {
        if (!cancelled) {
          const message =
            error instanceof Error ? error.message : 'Error loading cleaning tasks dashboard';
          setLoadError(message);
          if (!servedCachedDashboard) {
            setDashboard(null);
          }
          showErrorToast(message);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setIsDashboardUpdating(false);
        }
      }
    };

    loadDashboard();
    return () => {
      cancelled = true;
    };
  }, [cacheScope, showErrorToast, token, reloadKey]);

  const reloadDashboard = useCallback(() => {
    setReloadKey((current) => current + 1);
  }, []);

  const beginRefreshingChain = (chainId: number) => {
    setRefreshingChainIds((current) => new Set(current).add(chainId));
  };

  const finishRefreshingChain = (chainId: number) => {
    setRefreshingChainIds((current) => {
      const next = new Set(current);
      next.delete(chainId);
      return next;
    });
  };

  const handleRefreshChain = async (chainId: number) => {
    beginRefreshingChain(chainId);
    try {
      const run = await ForgeApiService.refreshCleaningChain(token, chainId);
      if (run.status === 'completed') {
        finishRefreshingChain(chainId);
        reloadDashboard();
        return;
      }
      if (run.status === 'failed') {
        finishRefreshingChain(chainId);
        showErrorToast(
          `Cleaning tasks refresh failed for chain ${chainId}${run.error ? `: ${run.error}` : ''}`
        );
        return;
      }
      setActiveRuns((current) => {
        if (current.some((activeRun) => activeRun.runId === run.runId)) return current;
        return [...current, { chainId, runId: run.runId }];
      });
    } catch (error) {
      finishRefreshingChain(chainId);
      showErrorToast(
        error instanceof Error ? error.message : 'Error refreshing cleaning tasks chain'
      );
    }
  };

  useEffect(() => {
    if (!token || activeRuns.length === 0) return undefined;
    let isCancelled = false;

    const pollRuns = async () => {
      const runsAtPollStart = activeRuns;
      const results = await Promise.allSettled(
        runsAtPollStart.map((run) => ForgeApiService.fetchCleaningRun(token, run.runId))
      );
      if (isCancelled) return;

      const stillRunning: ActiveCleaningRun[] = [];
      let anyCompleted = false;

      results.forEach((result, index) => {
        const activeRun = runsAtPollStart[index];
        if (result.status === 'rejected') {
          const failureCount = (pollFailureCountsRef.current.get(activeRun.runId) ?? 0) + 1;
          pollFailureCountsRef.current.set(activeRun.runId, failureCount);

          if (failureCount >= MAX_CONSECUTIVE_POLL_FAILURES) {
            pollFailureCountsRef.current.delete(activeRun.runId);
            pollErrorRunIdsRef.current.delete(activeRun.runId);
            finishRefreshingChain(activeRun.chainId);
            showErrorToast(
              `Cleaning tasks refresh for chain ${activeRun.chainId} could not be confirmed after repeated errors; please retry.`
            );
            return;
          }

          stillRunning.push(activeRun);
          if (!pollErrorRunIdsRef.current.has(activeRun.runId)) {
            pollErrorRunIdsRef.current.add(activeRun.runId);
            showErrorToast(
              result.reason instanceof Error
                ? result.reason.message
                : `Error checking cleaning tasks run ${activeRun.runId}`
            );
          }
          return;
        }

        pollFailureCountsRef.current.delete(activeRun.runId);

        const run: CleaningRefreshRun = result.value;
        if (run.status === 'completed') {
          anyCompleted = true;
          finishRefreshingChain(activeRun.chainId);
        } else if (run.status === 'failed') {
          finishRefreshingChain(activeRun.chainId);
          showErrorToast(
            `Cleaning tasks refresh failed for chain ${activeRun.chainId}${run.error ? `: ${run.error}` : ''}`
          );
        } else {
          stillRunning.push(activeRun);
        }
      });

      if (isCancelled) return;

      setActiveRuns((current) => {
        const polledRunIds = new Set(runsAtPollStart.map((run) => run.runId));
        const newRunsStartedDuringPoll = current.filter((run) => !polledRunIds.has(run.runId));
        return [...newRunsStartedDuringPoll, ...stillRunning];
      });

      if (anyCompleted) {
        reloadDashboard();
      }
    };

    const intervalId = window.setInterval(() => {
      void pollRuns();
    }, RUN_POLL_INTERVAL_MS);

    return () => {
      isCancelled = true;
      window.clearInterval(intervalId);
    };
  }, [activeRuns, reloadDashboard, showErrorToast, token]);

  const chains = useMemo(() => dashboard?.chains ?? [], [dashboard]);

  const totals = useMemo(
    () =>
      chains.reduce(
        (acc, chain) => ({
          items: acc.items + (chain.itemCount ?? 0),
          critical: acc.critical + (chain.severityCounts.critical ?? 0),
          high: acc.high + (chain.severityCounts.high ?? 0),
        }),
        { items: 0, critical: 0, high: 0 }
      ),
    [chains]
  );

  const summaryMetrics = [
    ['Items scored', totals.items],
    ['Critical', totals.critical],
    ['High', totals.high],
  ] as const;

  const chainTableColumnCount = 7;

  const supportedChainIds = useMemo(() => dashboard?.supportedChainIds ?? [], [dashboard]);
  const currentScoreVersion = dashboard?.currentScoreVersion ?? null;

  const [itemFilters, updateItemFilters] = useUrlFilterState(ITEMS_URL_FILTERS);
  const shouldUseUrlChainImmediately =
    itemFilters.chain !== null && dashboard === null && isLoading;
  const selectedChainId =
    itemFilters.chain !== null &&
    (shouldUseUrlChainImmediately || supportedChainIds.includes(itemFilters.chain))
      ? itemFilters.chain
      : (supportedChainIds[0] ?? null);
  const selectedChain = chains.find((chain) => chain.chainId === selectedChainId) ?? null;
  const selectedChainIsOldVersion =
    selectedChain?.scoreVersion != null &&
    currentScoreVersion != null &&
    selectedChain.scoreVersion !== currentScoreVersion;
  const familySupportsTrainingIssueFilters =
    itemFilters.family === 'combined' || itemFilters.family === 'training';
  const hasRequestedTrainingIssueFilters =
    familySupportsTrainingIssueFilters &&
    (itemFilters.issue.length > 0 || itemFilters.scope !== 'all');
  const isTrainingIssueFilterCapabilityPending =
    hasRequestedTrainingIssueFilters && selectedChain === null && isLoading;
  const supportsTrainingIssueFilters =
    familySupportsTrainingIssueFilters &&
    selectedChain?.scoreSchemaVersion != null &&
    selectedChain.scoreSchemaVersion >= TRAINING_ISSUE_FILTER_SCHEMA_VERSION;
  const effectiveIssueTypes = useMemo(() => {
    if (!supportsTrainingIssueFilters) return [];
    const requested = new Set(itemFilters.issue);
    return TRAINING_ISSUE_TYPE_OPTIONS.filter((option) => requested.has(option.value)).map(
      (option) => option.value
    );
  }, [itemFilters.issue, supportsTrainingIssueFilters]);
  const effectiveIssueScope = supportsTrainingIssueFilters ? itemFilters.scope : 'all';
  const effectiveModPrefixes = useMemo(
    () => [...new Set(itemFilters.mods.map((prefix) => prefix.trim().toUpperCase()).filter(Boolean))],
    [itemFilters.mods]
  );
  // Link/evidence rerouting only applies where the finding filter can also
  // shape the queue; on legacy chains the focus stays visually and behaviorally off.
  const confusionFocusActive = itemFilters.focus === 'confusion' && supportsTrainingIssueFilters;
  const effectiveFocusSubscores = useMemo(
    () => (confusionFocusActive ? CONFUSION_FOCUS_SUBSCORES : []),
    [confusionFocusActive]
  );
  const selectedSubscoreMetaByGroup = useMemo(
    () =>
      subscoreMetaByGroupForVersion(
        selectedChain?.scoreSchemaVersion,
        selectedChain?.scoreVersion,
        currentScoreVersion
      ),
    [currentScoreVersion, selectedChain?.scoreSchemaVersion, selectedChain?.scoreVersion]
  );
  const committedItemQuery = itemFilters.q;
  const [queryInput, setQueryInput] = useState(committedItemQuery);

  useEffect(() => {
    setQueryInput(committedItemQuery);
  }, [committedItemQuery]);

  const [items, setItems] = useState<CleaningItemScore[]>([]);
  const [itemsTotal, setItemsTotal] = useState(0);
  const [issueTypeCounts, setIssueTypeCounts] = useState<
    Partial<Record<CleaningIssueType, number>>
  >({});
  const [issueScopeCounts, setIssueScopeCounts] = useState<
    Partial<Record<CleaningIssueScope, number>>
  >({});
  const [modPrefixCounts, setModPrefixCounts] = useState<Record<string, number>>({});
  // True only until the first items response ever arrives; after that, refetches
  // (severity/page/sort/chain changes and post-refresh reloads) are silent so the
  // previously rendered rows stay visible instead of unmounting into a loading state.
  const [itemsLoading, setItemsLoading] = useState(true);
  const [isItemsUpdating, setIsItemsUpdating] = useState(false);
  const [itemsError, setItemsError] = useState<string | null>(null);
  const [expandedItemKeys, setExpandedItemKeys] = useState<Set<string>>(() => new Set());
  const [updatingExcludedItemKeys, setUpdatingExcludedItemKeys] = useState<Set<string>>(
    () => new Set()
  );
  const [isScoreExplainerOpen, setIsScoreExplainerOpen] = useState(false);
  const hasLoadedItemsOnceRef = useRef(false);
  const [chainStats, setChainStats] = useState<CleaningChainStatsResponse | null>(null);
  const [isChainStatsLoading, setIsChainStatsLoading] = useState(false);
  const [chainHistory, setChainHistory] = useState<CleaningChainHistoryResponse | null>(null);
  const chainHistoryCacheRef = useRef<Map<string, CleaningChainHistoryResponse | null>>(new Map());
  // Keyed by `${cacheScope}:${chainId}:${reloadKey}` so users cannot share
  // in-memory aggregates and a completed refresh (which bumps reloadKey)
  // naturally invalidates the cached aggregates for every chain.
  const chainStatsCacheRef = useRef<Map<string, CleaningChainStatsResponse | null>>(new Map());

  useEffect(() => {
    if (!token || selectedChainId === null) return undefined;
    const cacheKey = `${cacheScope ?? 'anonymous'}:${selectedChainId}:${reloadKey}`;
    const requestKey = `${selectedChainId}`;
    const cached = chainStatsCacheRef.current.get(cacheKey);
    if (cached !== undefined) {
      setChainStats(cached);
      return undefined;
    }
    let cancelled = false;
    let servedCachedStats = false;
    if (reloadKey === 0) {
      const cachedStats = readCleaningCacheEntry<CleaningChainStatsResponse | null>(
        cacheScope,
        CLEANING_CACHE_SLOTS.stats,
        requestKey
      );
      if (cachedStats) {
        servedCachedStats = true;
        setChainStats(cachedStats.data);
        setIsChainStatsLoading(false);
      } else {
        setIsChainStatsLoading(true);
      }
    } else {
      setIsChainStatsLoading(true);
    }
    ForgeApiService.fetchCleaningChainStats(token, selectedChainId)
      .then((stats) => {
        if (cancelled) return;
        chainStatsCacheRef.current.set(cacheKey, stats);
        setChainStats(stats);
        writeCleaningCacheSlot(cacheScope, CLEANING_CACHE_SLOTS.stats, requestKey, stats);
      })
      .catch(() => {
        // Stats are an auxiliary panel: hide it on errors instead of toasting.
        if (!cancelled && !servedCachedStats) setChainStats(null);
      })
      .finally(() => {
        if (!cancelled) setIsChainStatsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [cacheScope, token, selectedChainId, reloadKey]);

  useEffect(() => {
    if (!token || selectedChainId === null) return undefined;
    const cacheKey = `${cacheScope ?? 'anonymous'}:${selectedChainId}:${reloadKey}`;
    const requestKey = `${selectedChainId}`;
    const cached = chainHistoryCacheRef.current.get(cacheKey);
    if (cached !== undefined) {
      setChainHistory(cached);
      return undefined;
    }
    let cancelled = false;
    let servedCachedHistory = false;
    if (reloadKey === 0) {
      const cachedHistory = readCleaningCacheEntry<CleaningChainHistoryResponse | null>(
        cacheScope,
        CLEANING_CACHE_SLOTS.history,
        requestKey
      );
      if (cachedHistory) {
        servedCachedHistory = true;
        setChainHistory(cachedHistory.data);
      }
    }
    ForgeApiService.fetchCleaningChainHistory(token, selectedChainId)
      .then((history) => {
        if (cancelled) return;
        chainHistoryCacheRef.current.set(cacheKey, history);
        setChainHistory(history);
        writeCleaningCacheSlot(cacheScope, CLEANING_CACHE_SLOTS.history, requestKey, history);
      })
      .catch(() => {
        // The trend is an auxiliary panel: hide it on errors instead of toasting.
        if (!cancelled && !servedCachedHistory) setChainHistory(null);
      });
    return () => {
      cancelled = true;
    };
  }, [cacheScope, token, selectedChainId, reloadKey]);

  useEffect(() => {
    if (selectedChainId === null || isTrainingIssueFilterCapabilityPending) return undefined;
    let cancelled = false;
    const loadItems = async () => {
      const requestKey = cleaningItemsRequestKey({
        chainId: selectedChainId,
        family: itemFilters.family,
        severity: itemFilters.severity,
        excluded: itemFilters.excluded,
        duplicates: itemFilters.duplicates,
        issueTypes: effectiveIssueTypes,
        issueScope: effectiveIssueScope,
        locks: itemFilters.locks,
        modPrefixes: effectiveModPrefixes,
        focusSubscores: effectiveFocusSubscores,
        committedQuery: committedItemQuery,
        sort: itemFilters.sort,
        page: itemFilters.page,
      });
      let servedCachedItems = false;
      if (reloadKey === 0 && !hasLoadedItemsOnceRef.current) {
        const cachedItems = readCleaningCacheSlot<CleaningItemsResponse>(
          cacheScope,
          CLEANING_CACHE_SLOTS.items,
          requestKey
        );
        if (cachedItems) {
          servedCachedItems = true;
          setItems(cachedItems.items);
          setItemsTotal(cachedItems.total);
          setIssueTypeCounts(cachedItems.issueTypeCounts ?? {});
          setIssueScopeCounts(cachedItems.issueScopeCounts ?? {});
          setModPrefixCounts(cachedItems.modPrefixCounts ?? {});
          hasLoadedItemsOnceRef.current = true;
          setItemsLoading(false);
          setIsItemsUpdating(true);
        }
      }
      if (hasLoadedItemsOnceRef.current) {
        setIsItemsUpdating(true);
      } else {
        setItemsLoading(true);
      }
      try {
        const response = await ForgeApiService.fetchCleaningItems(token, {
          chainId: selectedChainId,
          ...(itemFilters.family !== 'training' ? { family: itemFilters.family } : {}),
          page: itemFilters.page,
          pageSize: ITEMS_PAGE_SIZE,
          severity: itemFilters.severity,
          excluded: itemFilters.excluded,
          ...(itemFilters.duplicates !== 'hide' ? { duplicates: itemFilters.duplicates } : {}),
          ...(effectiveIssueTypes.length > 0 ? { issueTypes: effectiveIssueTypes } : {}),
          ...(effectiveIssueScope !== 'all' ? { issueScope: effectiveIssueScope } : {}),
          ...(itemFilters.locks !== 'any' ? { locks: itemFilters.locks } : {}),
          ...(effectiveModPrefixes.length > 0 ? { modPrefixes: effectiveModPrefixes } : {}),
          ...(effectiveFocusSubscores.length > 0
            ? { focusSubscores: effectiveFocusSubscores }
            : {}),
          sort: itemFilters.sort,
          ...(committedItemQuery ? { search: committedItemQuery } : {}),
        });
        if (!cancelled) {
          setItemsError(null);
          setItems(response.items);
          setItemsTotal(response.total);
          setIssueTypeCounts(response.issueTypeCounts ?? {});
          setIssueScopeCounts(response.issueScopeCounts ?? {});
          setModPrefixCounts(response.modPrefixCounts ?? {});
          hasLoadedItemsOnceRef.current = true;
          writeCleaningCacheSlot(cacheScope, CLEANING_CACHE_SLOTS.items, requestKey, response);
        }
      } catch (error) {
        if (!cancelled) {
          const message =
            error instanceof Error ? error.message : 'Error loading cleaning tasks items';
          setItemsError(message);
          if (!servedCachedItems) {
            setItems([]);
            setItemsTotal(0);
            setIssueTypeCounts({});
            setIssueScopeCounts({});
            setModPrefixCounts({});
          }
          showErrorToast(message);
        }
      } finally {
        if (!cancelled) {
          setItemsLoading(false);
          setIsItemsUpdating(false);
        }
      }
    };

    loadItems();
    return () => {
      cancelled = true;
    };
  }, [
    token,
    cacheScope,
    selectedChainId,
    isTrainingIssueFilterCapabilityPending,
    itemFilters.page,
    itemFilters.family,
    itemFilters.severity,
    itemFilters.excluded,
    itemFilters.duplicates,
    effectiveIssueTypes,
    effectiveIssueScope,
    itemFilters.locks,
    effectiveModPrefixes,
    effectiveFocusSubscores,
    committedItemQuery,
    itemFilters.sort,
    showErrorToast,
    reloadKey,
  ]);

  const handleSelectSeverityTab = useCallback(
    (severity: CleaningSeverity | 'all') => {
      updateItemFilters({ severity, page: 1 });
    },
    [updateItemFilters]
  );

  const handleSelectFamily = useCallback(
    (family: CleaningFamily) => {
      updateItemFilters({
        family,
        sort: 'score',
        page: 1,
        // A manual family change leaves the confusion-focus preset; the finding
        // filter is derived from the focus, so nothing else needs clearing.
        ...(family !== 'training' ? { focus: 'all' as const } : {}),
        ...(family === 'inference' || family === 'catalog'
          ? { issue: [], scope: 'all' as const }
          : {}),
      });
    },
    [updateItemFilters]
  );

  const handleSelectIssueTypes = useCallback(
    (issue: CleaningIssueType[]) => {
      updateItemFilters({ issue, page: 1 });
    },
    [updateItemFilters]
  );

  const handleSelectIssueScope = useCallback(
    (scope: CleaningIssueScope | 'all') => {
      updateItemFilters({ scope, page: 1 });
    },
    [updateItemFilters]
  );

  const handleSelectLocks = useCallback(
    (locks: CleaningLockFilter) => {
      updateItemFilters({ locks, page: 1 });
    },
    [updateItemFilters]
  );

  const handleSelectModPrefixes = useCallback(
    (mods: string[]) => {
      updateItemFilters({ mods, page: 1 });
    },
    [updateItemFilters]
  );

  const handleSelectFocus = useCallback(
    (focus: 'all' | 'confusion') => {
      // Membership comes from the derived class_confusion finding filter, and the
      // Task type picker stays independent and editable afterwards — but a task-type
      // or scope filter left over from the previous queue would silently intersect
      // this one, so switching focus starts from a clean picker either way.
      if (focus === 'confusion') {
        updateItemFilters({
          focus,
          family: 'training',
          sort: CONFUSION_FOCUS_SORT,
          issue: [],
          scope: 'all',
          page: 1,
        });
        return;
      }
      updateItemFilters({ focus, sort: 'score', issue: [], scope: 'all', page: 1 });
    },
    [updateItemFilters]
  );

  const handleSelectQueueMode = useCallback(
    (mode: 'cleaning' | 'audit') => {
      updateItemFilters({ duplicates: mode === 'cleaning' ? 'hide' : 'show', page: 1 });
    },
    [updateItemFilters]
  );

  const handleSelectExcludedFilter = useCallback(
    (excluded: 'hide' | 'only') => {
      updateItemFilters({ excluded, page: 1 });
    },
    [updateItemFilters]
  );

  const handleSelectItemsChain = useCallback(
    (chainId: number) => {
      updateItemFilters({ chain: chainId, page: 1 });
    },
    [updateItemFilters]
  );

  const handleSelectSort = useCallback(
    (sort: CleaningSort) => {
      updateItemFilters({ sort, page: 1 });
    },
    [updateItemFilters]
  );

  const handleSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    updateItemFilters({ q: queryInput.trim(), page: 1 });
  };

  const clearSearch = () => {
    setQueryInput('');
    updateItemFilters({ q: '', page: 1 });
  };

  // Sort is always ascending (dirtiest first); there is no direction toggle, so the
  // active column is simply "ascending" and every other column is "none".
  const sortAriaSort = useCallback(
    (sort: CleaningSort): React.AriaAttributes['aria-sort'] =>
      itemFilters.sort === sort ? 'ascending' : 'none',
    [itemFilters.sort]
  );

  const sortIndicator = useCallback(
    (sort: CleaningSort) => (itemFilters.sort === sort ? ' ↑' : ''),
    [itemFilters.sort]
  );

  const [isExportingCsv, setIsExportingCsv] = useState(false);
  const handleExportCsv = useCallback(
    async (limit: number) => {
      if (selectedChainId === null || isExportingCsv) return;
      setIsExportingCsv(true);
      try {
        let scoreVersion: string | null = null;
        let computedAt: string | null = null;
        const all = await fetchPagedRows<CleaningItemScore>(
          async (page, pageSize) => {
            const response = await ForgeApiService.fetchCleaningItems(token, {
              chainId: selectedChainId,
              ...(itemFilters.family !== 'training' ? { family: itemFilters.family } : {}),
              page,
              pageSize,
              severity: itemFilters.severity,
              excluded: itemFilters.excluded,
              ...(itemFilters.duplicates !== 'hide' ? { duplicates: itemFilters.duplicates } : {}),
              ...(effectiveIssueTypes.length > 0 ? { issueTypes: effectiveIssueTypes } : {}),
              ...(effectiveIssueScope !== 'all' ? { issueScope: effectiveIssueScope } : {}),
              ...(itemFilters.locks !== 'any' ? { locks: itemFilters.locks } : {}),
              ...(effectiveModPrefixes.length > 0 ? { modPrefixes: effectiveModPrefixes } : {}),
              ...(effectiveFocusSubscores.length > 0
                ? { focusSubscores: effectiveFocusSubscores }
                : {}),
              sort: itemFilters.sort,
              ...(committedItemQuery ? { search: committedItemQuery } : {}),
            });
            scoreVersion = response.scoreVersion ?? scoreVersion;
            computedAt = response.computedAt ?? computedAt;
            return { rows: response.items, total: response.total };
          },
          { limit, pageSize: CSV_EXPORT_PAGE_SIZE }
        );
        const severitySuffix = itemFilters.severity === 'all' ? '' : `-${itemFilters.severity}`;
        downloadCsv(
          `cleaning-items-chain-${selectedChainId}${severitySuffix}.csv`,
          CSV_HEADERS,
          all
            // Every item with work in it belongs in the batch a labeler is given; a
            // perfect score has nothing to fix. Filtering to items flagged by the
            // focused check shipped ~3% of a focus queue.
            .filter(
              (item) =>
                familyScoreForItem(item, itemFilters.family).score < CLEANING_PERFECT_SCORE
            )
            .map((item) =>
              csvRowForItem(item, {
                scoreVersion,
                computedAt,
                family: itemFilters.family,
                focusSubscores: effectiveFocusSubscores,
              })
            )
        );
      } catch {
        showErrorToast('Failed to export cleaning items CSV');
      } finally {
        setIsExportingCsv(false);
      }
    },
    [
      selectedChainId,
      isExportingCsv,
      token,
      itemFilters.severity,
      itemFilters.family,
      itemFilters.excluded,
      itemFilters.duplicates,
      effectiveIssueTypes,
      effectiveIssueScope,
      itemFilters.locks,
      effectiveModPrefixes,
      effectiveFocusSubscores,
      itemFilters.sort,
      committedItemQuery,
      showErrorToast,
    ]
  );

  const handlePrevPage = useCallback(() => {
    updateItemFilters({ page: Math.max(1, itemFilters.page - 1) });
  }, [updateItemFilters, itemFilters.page]);

  const handleNextPage = useCallback(() => {
    updateItemFilters({ page: itemFilters.page + 1 });
  }, [updateItemFilters, itemFilters.page]);

  const toggleEvidenceExpanded = useCallback((itemKey: string) => {
    setExpandedItemKeys((current) => {
      const next = new Set(current);
      if (next.has(itemKey)) {
        next.delete(itemKey);
      } else {
        next.add(itemKey);
      }
      return next;
    });
  }, []);

  const beginUpdatingItemExclusion = (itemKey: string) => {
    setUpdatingExcludedItemKeys((current) => new Set(current).add(itemKey));
  };

  const finishUpdatingItemExclusion = (itemKey: string) => {
    setUpdatingExcludedItemKeys((current) => {
      const next = new Set(current);
      next.delete(itemKey);
      return next;
    });
  };

  const handleToggleItemExcluded = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement>, item: CleaningItemScore) => {
      event.stopPropagation();
      if (selectedChainId === null) return;

      const nextExcluded = !item.excluded;
      beginUpdatingItemExclusion(item.itemKey);
      try {
        await ForgeApiService.setCleaningItemExcluded(
          token,
          selectedChainId,
          item.itemKey,
          nextExcluded
        );
        showSuccessToast(nextExcluded ? 'Marked as no changes needed' : 'Item reopened');
        setReloadKey((current) => current + 1);
      } catch (error) {
        showErrorToast(
          error instanceof Error ? error.message : 'Error updating cleaning item exclusion'
        );
      } finally {
        finishUpdatingItemExclusion(item.itemKey);
      }
    },
    [selectedChainId, showErrorToast, showSuccessToast, token]
  );

  const totalPages = Math.max(1, Math.ceil(itemsTotal / ITEMS_PAGE_SIZE));
  const showTrainingCoherenceDiagnostics =
    itemFilters.family === 'training' &&
    selectedChain?.scoreVersion === dashboard?.currentScoreVersion;
  const tableSubscoreMetaByGroup = useMemo(
    () =>
      SUBSCORE_GROUPS.reduce(
        (acc, group) => {
          const familyIsVisible =
            itemFilters.family === 'combined' || group.key === itemFilters.family;
          const selectedEntries = familyIsVisible ? selectedSubscoreMetaByGroup[group.key] : [];
          if (
            group.key === 'training' &&
            showTrainingCoherenceDiagnostics &&
            selectedEntries.some((entry) => entry.key === 'classCoherence')
          ) {
            const coherenceIndex = selectedEntries.findIndex(
              (entry) => entry.key === 'classCoherence'
            );
            acc[group.key] = [
              ...selectedEntries.slice(0, coherenceIndex + 1),
              ...TRAINING_COHERENCE_DIAGNOSTIC_META,
              ...selectedEntries.slice(coherenceIndex + 1),
            ];
          } else {
            acc[group.key] = selectedEntries;
          }
          return acc;
        },
        {} as Record<SubscoreGroupKey, SubscoreMetaEntry[]>
      ),
    [itemFilters.family, selectedSubscoreMetaByGroup, showTrainingCoherenceDiagnostics]
  );
  const tableScoreFamilies: CleaningFamily[] =
    itemFilters.family === 'combined'
      ? ['combined', 'training', 'inference', 'catalog']
      : [itemFilters.family];
  // Expander, identifier, one or four score columns, and the subscore strip.
  const itemsTableColumnCount = 3 + tableScoreFamilies.length;

  return (
    <div
      className={`page-content page-content--wide rdc-dashboard-page cleaning-dashboard-page ${
        itemFilters.duplicates === 'hide' ? 'is-cleaning-mode' : 'is-audit-mode'
      }`}
    >
      <section className="forge-card rdc-dashboard-hero cleaning-dashboard-hero">
        <div className="rdc-dashboard-hero-grid">
          <div>
            <div className="rdc-dashboard-eyebrow">Task Lists · Model readiness</div>
            <h1 className="rdc-dashboard-title">Cleaning tasks</h1>
            <div className="rdc-dashboard-copy">
              A ranked work queue for cleaning the classes used by training and inference. Start at
              the top: linked follow-up rows can be collapsed automatically.
            </div>
            <div className="cleaning-supported-chains">
              Supported chains: {dashboard?.supportedChainIds?.join(', ') || '-'}
            </div>
          </div>
          <div className="cleaning-hero-metrics">
            {summaryMetrics.map(([label, value]) => (
              <div key={label} className="cleaning-metric-tile">
                <div className="cleaning-metric-label">{label}</div>
                <div className="cleaning-metric-value">{formatNumber(Number(value))}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {
        // prettier-ignore
        (() => {
        const overviewSections = (
          <>
      <section
        className="forge-card rdc-dashboard-card cleaning-dashboard-card cleaning-chains-card"
        data-testid="cleaning-chains-card"
      >
        <div
          className="forge-card-title rdc-dashboard-card-title rdc-dashboard-table-title"
          style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}
        >
          <span>Chains</span>
        </div>
        <div
          className={`forge-busy-container${isDashboardUpdating ? ' is-busy' : ''}`}
          aria-busy={isDashboardUpdating}
        >
          {isDashboardUpdating ? (
            <TableBusyOverlay label="Updating chains..." testId="chains-updating-indicator" />
          ) : null}
          <div style={{ overflowX: 'auto' }}>
            <table className="cleaning-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={headerCellStyle}>Chain</th>
                  <th style={headerCellStyle}>Last refreshed</th>
                  <th style={headerCellStyle}>Score version</th>
                  <th style={headerCellStyle}>Embedding model</th>
                  <th style={headerCellStyle}>Items / severity</th>
                  <th style={headerCellStyle}>Mean score</th>
                  <th style={headerCellStyle}>Action</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={chainTableColumnCount} style={bodyCellStyle}>
                      Loading snapshots...
                    </td>
                  </tr>
                ) : loadError && chains.length === 0 ? (
                  <tr>
                    <td
                      colSpan={chainTableColumnCount}
                      style={{ ...bodyCellStyle, color: 'var(--danger)' }}
                    >
                      {loadError}
                    </td>
                  </tr>
                ) : chains.length === 0 ? (
                  <tr>
                    <td colSpan={chainTableColumnCount} style={bodyCellStyle}>
                      No supported chains.
                    </td>
                  </tr>
                ) : (
                  chains.map((chain) => {
                    const isRefreshing = refreshingChainIds.has(chain.chainId);
                    const neverRefreshed = !chain.computedAt;
                    const isSelected = chain.chainId === selectedChainId;
                    const isOldScoreVersion =
                      chain.scoreVersion != null &&
                      currentScoreVersion != null &&
                      chain.scoreVersion !== currentScoreVersion;
                    const familyMeanScore =
                      itemFilters.family === 'combined'
                        ? chain.meanScore
                        : (chain.familyMeanScores?.[itemFilters.family] ?? chain.meanScore);
                    const familySeverityCounts =
                      itemFilters.family === 'combined'
                        ? chain.severityCounts
                        : (chain.familySeverityCounts?.[itemFilters.family] ??
                          chain.severityCounts);
                    return (
                      <tr
                        key={chain.chainId}
                        data-testid="cleaning-chain-row"
                        data-score-version-status={isOldScoreVersion ? 'old' : 'current'}
                        aria-selected={isSelected}
                        tabIndex={0}
                        title={
                          isOldScoreVersion
                            ? `Show this chain's version ${chain.scoreVersion} items (current version: ${currentScoreVersion})`
                            : "Show this chain's items below"
                        }
                        style={{
                          cursor: 'pointer',
                          background: isSelected
                            ? 'color-mix(in srgb, var(--accent) 7%, transparent)'
                            : isOldScoreVersion
                              ? 'color-mix(in srgb, var(--text-tertiary) 5%, transparent)'
                              : undefined,
                          boxShadow: isSelected ? 'inset 2px 0 0 var(--accent)' : undefined,
                          opacity: isOldScoreVersion ? 0.62 : 1,
                        }}
                        onClick={() => handleSelectItemsChain(chain.chainId)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            handleSelectItemsChain(chain.chainId);
                          }
                        }}
                      >
                        <td style={bodyCellStyle}>
                          <div style={{ color: 'var(--text-primary)', fontWeight: 800 }}>
                            {getForgeChainLabel(chain.chainId)}
                          </div>
                          <div style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>
                            chain {chain.chainId}
                          </div>
                        </td>
                        <td style={bodyCellStyle}>
                          {neverRefreshed ? (
                            <span style={{ color: 'var(--warning)' }}>Never refreshed</span>
                          ) : (
                            <>
                              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                                <span
                                  data-testid="cleaning-chain-age"
                                  style={{
                                    fontSize: 11,
                                    fontWeight: 800,
                                    color: (() => {
                                      const ageSeconds = ageSecondsFromTimestamp(chain.computedAt);
                                      return ageSeconds !== undefined
                                        ? snapshotAgeColor(ageSeconds)
                                        : undefined;
                                    })(),
                                  }}
                                >
                                  {formatRelativeAge(ageSecondsFromTimestamp(chain.computedAt))}
                                </span>
                                {chain.lastRefreshSeconds != null ? (
                                  <span
                                    title="Duration of the last refresh"
                                    style={{
                                      color: 'var(--text-tertiary)',
                                      fontSize: 11,
                                      fontWeight: 800,
                                      fontVariantNumeric: 'tabular-nums',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {formatDuration(chain.lastRefreshSeconds * 1000)}
                                  </span>
                                ) : null}
                              </div>
                              <div style={{ color: 'var(--text-tertiary)', fontSize: 11 }}>
                                {formatTimestamp(chain.computedAt)}
                              </div>
                            </>
                          )}
                        </td>
                        <td style={monoCellStyle}>
                          <div>{chain.scoreVersion ?? '-'}</div>
                          {isOldScoreVersion ? (
                            <span
                              data-testid="cleaning-old-version-label"
                              style={{
                                color: 'var(--text-tertiary)',
                                fontFamily: 'var(--font-sans)',
                                fontSize: 10,
                                fontWeight: 800,
                              }}
                            >
                              old version
                            </span>
                          ) : null}
                        </td>
                        <td style={monoCellStyle}>{chain.embeddingModelVersion ?? '-'}</td>
                        <td style={bodyCellStyle}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 800 }}>
                              {formatNumber(chain.itemCount)}
                            </span>
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                              {SEVERITY_ORDER.filter(
                                (severity) => (familySeverityCounts[severity] ?? 0) > 0
                              ).map((severity) => (
                                <span
                                  key={severity}
                                  title={`${itemFilters.family} ${severity}: ${familySeverityCounts[severity]}`}
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 4,
                                    fontSize: 10,
                                    fontWeight: 800,
                                    color: 'var(--text-secondary)',
                                  }}
                                >
                                  <span
                                    style={{
                                      width: 7,
                                      height: 7,
                                      borderRadius: '50%',
                                      background: SEVERITY_COLORS[severity],
                                    }}
                                  />
                                  {familySeverityCounts[severity]}
                                </span>
                              ))}
                            </div>
                          </div>
                        </td>
                        <td
                          style={{
                            ...monoCellStyle,
                            color: meanScoreColor(familyMeanScore),
                            fontWeight: 800,
                          }}
                          data-mean-band={
                            familyMeanScore === null || familyMeanScore === undefined
                              ? undefined
                              : familyMeanScore < 90
                                ? 'red'
                                : familyMeanScore <= 95
                                  ? 'yellow'
                                  : 'green'
                          }
                        >
                          {formatMeanScore(familyMeanScore)}
                        </td>
                        <td style={bodyCellStyle}>
                          <button
                            type="button"
                            style={{
                              ...refreshButtonStyle,
                              opacity: isRefreshing ? 0.65 : 1,
                              cursor: isRefreshing ? 'not-allowed' : 'pointer',
                            }}
                            disabled={isRefreshing}
                            onClick={(event) => {
                              event.stopPropagation();
                              void handleRefreshChain(chain.chainId);
                            }}
                          >
                            <RefreshOutlinedIcon style={{ fontSize: 15 }} />
                            {isRefreshing ? 'Refreshing...' : 'Refresh'}
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {chainStats && chainStats.itemCount > 0 ? (
        <section
          className="forge-card rdc-dashboard-card cleaning-dashboard-card cleaning-stats-card"
          data-testid="cleaning-chain-stats-card"
        >
          <ChainStatsBand
            stats={chainStats}
            history={chainHistory}
            scoreSchemaVersion={selectedChain?.scoreSchemaVersion ?? null}
            currentScoreVersion={currentScoreVersion}
            family={itemFilters.family}
            activeSort={itemFilters.sort}
            onSelectSort={handleSelectSort}
          />
        </section>
      ) : isChainStatsLoading ? (
        <section
          className="forge-card rdc-dashboard-card cleaning-dashboard-card cleaning-stats-card"
          data-testid="cleaning-chain-stats-card"
        >
          <div
            className="dc-skeleton"
            data-testid="cleaning-chain-stats-loading"
            style={{ height: 64 }}
          />
        </section>
      ) : null}
          </>
        );
        const taskQueueSection = (
      <section
        className="forge-card rdc-dashboard-card cleaning-dashboard-card cleaning-items-card"
        data-testid="cleaning-items-card"
      >
        <div className="forge-card-title cleaning-queue-header">
          <div className="cleaning-queue-title-row">
            <div className="cleaning-items-heading">
              <span>Task queue</span>
              <span className="cleaning-count-pill">{formatNumber(itemsTotal)} tasks</span>
            </div>
            <button
              type="button"
              className="rdc-dashboard-details-button"
              aria-haspopup="dialog"
              onClick={() => setIsScoreExplainerOpen(true)}
            >
              <InfoOutlinedIcon style={{ fontSize: 15 }} />
              Score guide
            </button>
          </div>

          <div className="cleaning-queue-family-row">
            <div className="cleaning-family-tabs" role="group" aria-label="Task family">
              {FAMILY_OPTIONS.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  aria-pressed={itemFilters.family === option.key}
                  style={itemFilters.family === option.key ? tabButtonActiveStyle : tabButtonStyle}
                  title={option.description}
                  onClick={() => handleSelectFamily(option.key)}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <div className="cleaning-queue-actions">
              <div
                role="group"
                aria-label="Queue mode"
                style={{ display: 'flex', alignItems: 'center', gap: 2 }}
              >
                <button
                  type="button"
                  className="cleaning-covered-task-toggle"
                  aria-pressed={itemFilters.duplicates === 'hide'}
                  style={itemFilters.duplicates === 'hide' ? tabButtonActiveStyle : tabButtonStyle}
                  title="Cleaning mode groups related work by hiding Item-number rows already covered by a stronger task; hidden rows still count in statistics"
                  onClick={() => handleSelectQueueMode('cleaning')}
                >
                  Cleaning
                </button>
                <button
                  type="button"
                  className="cleaning-covered-task-toggle"
                  aria-pressed={itemFilters.duplicates === 'show'}
                  style={itemFilters.duplicates === 'show' ? tabButtonActiveStyle : tabButtonStyle}
                  title="Audit mode shows every Item number or unresolved normalized barcode separately"
                  onClick={() => handleSelectQueueMode('audit')}
                >
                  Audit
                </button>
              </div>
              <CsvExportControl
                total={itemsLoading || isItemsUpdating ? null : itemsTotal}
                exporting={isExportingCsv}
                disabled={selectedChainId === null}
                showLimitControl={false}
                onExport={(limit) => {
                  void handleExportCsv(limit);
                }}
              />
            </div>
          </div>

          <div className="cleaning-queue-mode-note" data-testid="cleaning-queue-mode-note">
            {itemFilters.duplicates === 'hide'
              ? 'Cleaning task list: a task is hidden when an earlier visible task already covers any of the same Item numbers. Hidden tasks still count in audit statistics.'
              : 'Audit view: every scored Item number — or unresolved normalized barcode — is shown separately.'}
          </div>

          <div className="cleaning-queue-filter-row">
            <label
              className="cleaning-filter-control cleaning-filter-control--chain"
              htmlFor="cleaning-items-chain-select"
            >
              <span>Chain</span>
              <select
                id="cleaning-items-chain-select"
                aria-label="Chain"
                style={selectStyle}
                value={selectedChainId ?? ''}
                onChange={(event) => handleSelectItemsChain(Number(event.target.value))}
              >
                {supportedChainIds.map((chainId) => (
                  <option key={chainId} value={chainId}>
                    {getForgeChainLabel(chainId)}
                  </option>
                ))}
              </select>
            </label>
            <label className="cleaning-filter-control" htmlFor="cleaning-severity-select">
              <span>Severity</span>
              <select
                id="cleaning-severity-select"
                aria-label="Severity"
                style={selectStyle}
                value={itemFilters.severity}
                onChange={(event) =>
                  handleSelectSeverityTab(event.target.value as CleaningSeverity | 'all')
                }
              >
                {SEVERITY_TAB_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="cleaning-filter-control" htmlFor="cleaning-status-select">
              <span>Status</span>
              <select
                id="cleaning-status-select"
                aria-label="Status"
                style={selectStyle}
                value={itemFilters.excluded}
                onChange={(event) =>
                  handleSelectExcludedFilter(event.target.value as 'hide' | 'only')
                }
              >
                {EXCLUDED_FILTER_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            {supportsTrainingIssueFilters &&
            (itemFilters.family === 'combined' || itemFilters.family === 'training') ? (
              <>
                <label className="cleaning-filter-control" htmlFor="cleaning-focus-select">
                  <span>Focus</span>
                  <select
                    id="cleaning-focus-select"
                    aria-label="Task focus"
                    style={selectStyle}
                    value={itemFilters.focus}
                    onChange={(event) =>
                      handleSelectFocus(event.target.value as 'all' | 'confusion')
                    }
                  >
                    <option value="all">All tasks</option>
                    <option value="confusion">Confusion only</option>
                  </select>
                </label>
                <div className="cleaning-filter-control cleaning-filter-control--issue">
                  <span>Task type</span>
                  <DashboardCountedMultiSelect
                    ariaLabel="Training task types"
                    allLabel="All task types"
                    selectedLabel="task types"
                    selected={effectiveIssueTypes}
                    options={TRAINING_ISSUE_TYPE_OPTIONS.map((option) => ({
                      ...option,
                      code: TRAINING_ISSUE_METADATA[option.value].code,
                      count: issueTypeCounts[option.value] ?? 0,
                    }))}
                    onChange={handleSelectIssueTypes}
                  />
                </div>
                <label
                  className="cleaning-filter-control cleaning-filter-control--scope"
                  htmlFor="cleaning-scope-select"
                >
                  <span>Scope</span>
                  <select
                    id="cleaning-scope-select"
                    aria-label="Training issue scope"
                    style={selectStyle}
                    value={itemFilters.scope}
                    onChange={(event) =>
                      handleSelectIssueScope(event.target.value as CleaningIssueScope | 'all')
                    }
                  >
                    {TRAINING_ISSUE_SCOPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                        {option.value !== 'all' && issueScopeCounts[option.value] !== undefined
                          ? ` (${formatNumber(issueScopeCounts[option.value] ?? 0)})`
                          : ''}
                      </option>
                    ))}
                  </select>
                </label>
              </>
            ) : null}
            <label className="cleaning-filter-control" htmlFor="cleaning-locks-select">
              <span>Locks</span>
              <select
                id="cleaning-locks-select"
                aria-label="Design lock coverage"
                style={selectStyle}
                value={itemFilters.locks}
                onChange={(event) => handleSelectLocks(event.target.value as CleaningLockFilter)}
              >
                {LOCK_FILTER_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <div className="cleaning-filter-control cleaning-filter-control--issue">
              <span>Mod prefix</span>
              <DashboardCountedMultiSelect
                ariaLabel="Mod prefixes"
                allLabel="All mod prefixes"
                selectedLabel="mod prefixes"
                selected={effectiveModPrefixes}
                options={[...new Set([...Object.keys(modPrefixCounts), ...effectiveModPrefixes])]
                  .sort()
                  .map((prefix) => ({
                    value: prefix,
                    label: prefix === MOD_PREFIX_UNKNOWN ? 'Unknown' : prefix,
                    count: modPrefixCounts[prefix] ?? 0,
                  }))}
                onChange={handleSelectModPrefixes}
              />
            </div>
            <form className="cleaning-queue-search" onSubmit={handleSearchSubmit}>
              <div className="dg-search dg-search--compact">
                <SearchOutlinedIcon />
                <input
                  type="search"
                  aria-label="Search item numbers or barcodes"
                  placeholder="Item number or barcode"
                  value={queryInput}
                  onChange={(event) => setQueryInput(event.target.value)}
                />
              </div>
              {committedItemQuery ? (
                <button type="button" className="dg-search-clear" onClick={clearSearch}>
                  Clear
                </button>
              ) : null}
              <button type="submit" className="dg-primary-button">
                Search
              </button>
            </form>
          </div>
        </div>
        {selectedChainIsOldVersion ? (
          <div
            data-testid="cleaning-old-version-notice"
            style={{
              margin: '0 0 12px',
              padding: '8px 10px',
              border: '1px solid var(--border)',
              borderRadius: 6,
              background: 'color-mix(in srgb, var(--text-tertiary) 7%, transparent)',
              color: 'var(--text-tertiary)',
              fontSize: 11,
            }}
          >
            Showing old score version {selectedChain?.scoreVersion}; current version is{' '}
            {currentScoreVersion}. Refresh this chain to recalculate it.
          </div>
        ) : null}
        <div
          className={`forge-busy-container${isItemsUpdating ? ' is-busy' : ''}`}
          aria-busy={isItemsUpdating}
        >
          {isItemsUpdating ? (
            <TableBusyOverlay label="Updating items..." testId="items-updating-indicator" />
          ) : null}
          <div style={{ overflowX: 'auto' }}>
            <table
              className="cleaning-table cleaning-items-table"
              style={{ width: '100%', borderCollapse: 'collapse' }}
            >
              <colgroup>
                <col style={{ width: 36 }} />
                <col style={{ width: 230 }} />
                {tableScoreFamilies.map((family) => (
                  <col key={family} style={{ width: 82 }} />
                ))}
                <col />
              </colgroup>
              <thead>
                <tr>
                  <th style={headerCellStyle}></th>
                  <th style={headerCellStyle}>Identifier</th>
                  {tableScoreFamilies.map((family, index) => (
                    <th
                      key={family}
                      className="cleaning-family-score-header"
                      style={
                        index === 0
                          ? sortableFamilyScoreHeaderCellStyle
                          : familyScoreHeaderCellStyle
                      }
                      aria-sort={index === 0 ? sortAriaSort('score') : undefined}
                      onClick={index === 0 ? () => handleSelectSort('score') : undefined}
                    >
                      <span className="cleaning-family-score-heading">
                        <span>{FAMILY_LABELS[family]}</span>
                        <span>score{index === 0 ? sortIndicator('score') : null}</span>
                      </span>
                    </th>
                  ))}
                  <th style={headerCellStyle}>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'end' }}>
                      {SUBSCORE_GROUPS.filter(
                        (group) => tableSubscoreMetaByGroup[group.key].length > 0
                      ).map((group, groupIndex) => {
                        const entries = tableSubscoreMetaByGroup[group.key];
                        return (
                          <React.Fragment key={group.key}>
                            {groupIndex > 0 ? (
                              <span aria-hidden="true" style={subscoreGroupDividerStyle} />
                            ) : null}
                            <div style={{ display: 'grid', gap: 3, justifyItems: 'center' }}>
                              <span
                                style={subscoreGroupCaptionStyle}
                                title={`${group.label} — ${group.description}`}
                              >
                                <span style={subscoreGroupDotStyle(group.color)} />
                                {group.stripLabel}
                              </span>
                              <div style={{ display: 'flex', gap: 2 }}>
                                {entries.map((field) => (
                                  <span
                                    key={field.key}
                                    role="columnheader"
                                    aria-label={field.short}
                                    aria-sort={sortAriaSort(field.sort)}
                                    tabIndex={0}
                                    title={`${field.full}${
                                      field.diagnostic
                                        ? ' — diagnostic only; does not affect the Training score'
                                        : ''
                                    } — click to sort`}
                                    style={{
                                      ...subscoreSortHeaderStyle,
                                      color:
                                        itemFilters.sort === field.sort
                                          ? 'var(--text-primary)'
                                          : 'var(--text-tertiary)',
                                    }}
                                    onClick={() => handleSelectSort(field.sort)}
                                    onKeyDown={(event) => {
                                      if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        handleSelectSort(field.sort);
                                      }
                                    }}
                                  >
                                    <span
                                      style={{ display: 'grid', justifyItems: 'center', gap: 1 }}
                                    >
                                      <span>{subscoreIndexLabel(field)}</span>
                                      <span
                                        style={{ fontSize: 7.5, fontWeight: 700, opacity: 0.9 }}
                                      >
                                        {subscoreShortName(field)}
                                      </span>
                                    </span>
                                    {itemFilters.sort === field.sort ? (
                                      <span
                                        aria-hidden="true"
                                        style={{
                                          position: 'absolute',
                                          right: 3,
                                          top: 0,
                                          fontSize: 9,
                                        }}
                                      >
                                        ↑
                                      </span>
                                    ) : null}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </React.Fragment>
                        );
                      })}
                      {itemFilters.family === 'training' ? (
                        <span className="cleaning-training-task-heading">Task type</span>
                      ) : null}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody style={{ opacity: selectedChainIsOldVersion ? 0.72 : 1 }}>
                {selectedChainId === null ? (
                  <tr>
                    <td colSpan={itemsTableColumnCount} style={bodyCellStyle}>
                      No supported chains.
                    </td>
                  </tr>
                ) : itemsLoading ? (
                  <tr>
                    <td colSpan={itemsTableColumnCount} style={bodyCellStyle}>
                      Loading items...
                    </td>
                  </tr>
                ) : itemsError && items.length === 0 ? (
                  <tr>
                    <td
                      colSpan={itemsTableColumnCount}
                      style={{ ...bodyCellStyle, color: 'var(--danger)' }}
                    >
                      {itemsError}
                    </td>
                  </tr>
                ) : items.length === 0 ? (
                  <tr>
                    <td colSpan={itemsTableColumnCount} style={bodyCellStyle}>
                      No items match this filter.
                    </td>
                  </tr>
                ) : (
                  items.map((item) => {
                    const isExpanded = expandedItemKeys.has(item.itemKey);
                    const familyScore = familyScoreForItem(item, itemFilters.family);
                    // Confusion focus routes the labeler links (and their evidence
                    // preview) to the Confusion finding instead of the worst check.
                    const linkScore = focusedFamilyScore(
                      item,
                      familyScore,
                      effectiveFocusSubscores
                    );
                    const masterLabelUrl = masterLabelUrlForItem(
                      item,
                      itemFilters.family,
                      linkScore
                    );
                    const masterLabelDescription = masterLabelLinkDescription(
                      item,
                      itemFilters.family,
                      linkScore
                    );
                    const masterLabelDetailsSummary = masterLabelDetailsHighlightSummary(
                      item,
                      itemFilters.family,
                      linkScore
                    );
                    const evidenceCrops = linkScore.finding?.evidenceCrops ?? [];
                    const coveredByItemKey = item.taskCoveredBy?.[itemFilters.family];
                    const isUpdatingExclusion = updatingExcludedItemKeys.has(item.itemKey);
                    const countRows = cleaningCountRows(item);
                    const findingBySubscore = new Map(
                      (item.findings ?? []).map((finding) => [finding.subscore ?? '', finding])
                    );
                    return (
                      <React.Fragment key={item.itemKey}>
                        <tr data-testid="cleaning-item-row">
                          <td className="cleaning-item-expand-cell" style={bodyCellStyle}>
                            <button
                              type="button"
                              aria-label={isExpanded ? 'Hide details' : 'Show details'}
                              style={{
                                ...pageButtonStyle,
                                transform: isExpanded ? 'rotate(180deg)' : undefined,
                              }}
                              onClick={() => toggleEvidenceExpanded(item.itemKey)}
                            >
                              <ExpandMoreOutlinedIcon style={{ fontSize: 16 }} />
                            </button>
                          </td>
                          <td className="cleaning-item-identifier-cell" style={bodyCellStyle}>
                            <div className="cleaning-item-identifier-line">
                              <span
                                style={{
                                  fontFamily: 'var(--font-mono)',
                                  fontSize: 12,
                                  fontWeight: 800,
                                }}
                              >
                                {masterLabelUrl ? (
                                  <MasterLabelLink
                                    to={masterLabelUrl}
                                    variant="forge"
                                    title={masterLabelDescription}
                                    aria-label={masterLabelDescription}
                                  >
                                    {itemIdentifierLabel(item)}
                                  </MasterLabelLink>
                                ) : (
                                  <span style={{ color: 'var(--text-primary)' }}>
                                    {itemIdentifierLabel(item)}
                                  </span>
                                )}
                              </span>
                              {modPrefixChip(item)}
                              {lockStatusBadge(item)}
                              {itemStatusBadge(item)}
                              {itemFilters.duplicates === 'show' && coveredByItemKey ? (
                                <span
                                  className="cleaning-covered-task-badge"
                                  title={`This task is already covered by the lower-ranked ${itemKeyLabel(
                                    coveredByItemKey
                                  )} link`}
                                >
                                  covered by {itemKeyLabel(coveredByItemKey)}
                                </span>
                              ) : null}
                            </div>
                            {item.itemNumber && item.normalizedBarcodes.length > 0 ? (
                              <div className="cleaning-item-barcode">
                                nb:{item.normalizedBarcodes[0]}
                              </div>
                            ) : null}
                            {item.productName ? (
                              <div className="cleaning-product-name" title={item.productName}>
                                {item.productName}
                              </div>
                            ) : null}
                          </td>
                          {tableScoreFamilies.map((scoreFamily) => {
                            const score = familyScoreForItem(item, scoreFamily);
                            return (
                              <td
                                key={scoreFamily}
                                className="cleaning-family-score-cell"
                                style={{
                                  ...monoCellStyle,
                                  width: 82,
                                  minWidth: 82,
                                  maxWidth: 92,
                                  padding: '9px 4px',
                                  textAlign: 'center',
                                  color: SEVERITY_COLORS[score.severity],
                                  fontWeight: 800,
                                }}
                                data-score-family={scoreFamily}
                                data-severity={score.severity}
                                title={`${scoreFamily} severity: ${score.severity}`}
                              >
                                {formatScore(score.score)}
                                {(itemFilters.family === 'combined' &&
                                  scoreFamily === 'combined') ||
                                (itemFilters.family === 'training' && scoreFamily === 'training')
                                  ? scoreDeltaIndicator(item)
                                  : null}
                              </td>
                            );
                          })}
                          <td style={bodyCellStyle}>
                            <div
                              style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}
                            >
                              {SUBSCORE_GROUPS.filter(
                                (group) => tableSubscoreMetaByGroup[group.key].length > 0
                              ).map((group, groupIndex) => {
                                const entries = tableSubscoreMetaByGroup[group.key];
                                return (
                                  <React.Fragment key={group.key}>
                                    {groupIndex > 0 ? (
                                      <span aria-hidden="true" style={subscoreGroupDividerStyle} />
                                    ) : null}
                                    <div style={{ display: 'flex', gap: 2 }}>
                                      {entries.map((field) => {
                                        const value = subscoreValueForItem(item, field);
                                        const chipSeverity = subscoreChipSeverity(
                                          field,
                                          value,
                                          selectedChain?.scoreVersion
                                        );
                                        const isWorst =
                                          field.sort === familyScore.worstSubscore &&
                                          familyScore.severity !== 'clean';
                                        const coherenceFinding =
                                          findingBySubscore.get('class_coherence');
                                        const chipFinding =
                                          findingBySubscore.get(field.sort) ??
                                          (field.diagnostic &&
                                          coherenceFinding?.diagnosis === field.diagnostic
                                            ? coherenceFinding
                                            : undefined);
                                        const chipUrl = masterLabelUrlForParams(
                                          item.chainId,
                                          chipFinding?.masterLabelParams
                                        );
                                        const chipContext = `${field.full}: ${formatSubscoreValue(
                                          field,
                                          value,
                                          selectedChain?.scoreVersion
                                        )}${isWorst ? ' — worst check, drives the link' : ''}`;
                                        const chipLinkTitle = chipFinding?.message
                                          ? `${chipContext} — ${chipFinding.message}`
                                          : chipContext;
                                        if (chipUrl) {
                                          return (
                                            <MasterLabelLink
                                              key={field.key}
                                              to={chipUrl}
                                              variant="forge"
                                              data-testid={`subscore-chip-${field.key}`}
                                              data-severity={chipSeverity}
                                              data-worst={isWorst ? 'true' : undefined}
                                              data-clickable="true"
                                              title={chipLinkTitle}
                                              aria-label={chipLinkTitle}
                                              style={{
                                                ...subscoreValueCellStyle,
                                                color: SUBSCORE_CHIP_COLORS[chipSeverity],
                                                background: SUBSCORE_CHIP_BACKGROUNDS[chipSeverity],
                                                textDecoration: 'none',
                                                cursor: 'pointer',
                                                ...(isWorst
                                                  ? { boxShadow: 'inset 0 0 0 1.5px currentColor' }
                                                  : undefined),
                                              }}
                                            >
                                              <span style={{ fontSize: 9, opacity: 0.7 }}>
                                                {subscoreIndexLabel(field)}
                                              </span>
                                              <span style={{ fontSize: 10.5, fontWeight: 800 }}>
                                                {formatSubscoreValue(
                                                  field,
                                                  value,
                                                  selectedChain?.scoreVersion
                                                )}
                                              </span>
                                            </MasterLabelLink>
                                          );
                                        }
                                        return (
                                          <span
                                            key={field.key}
                                            data-testid={`subscore-chip-${field.key}`}
                                            data-severity={chipSeverity}
                                            data-worst={isWorst ? 'true' : undefined}
                                            title={chipContext}
                                            style={{
                                              ...subscoreValueCellStyle,
                                              color: SUBSCORE_CHIP_COLORS[chipSeverity],
                                              background: SUBSCORE_CHIP_BACKGROUNDS[chipSeverity],
                                              ...(isWorst
                                                ? { boxShadow: 'inset 0 0 0 1.5px currentColor' }
                                                : undefined),
                                            }}
                                          >
                                            <span style={{ fontSize: 9, opacity: 0.7 }}>
                                              {subscoreIndexLabel(field)}
                                            </span>
                                            <span style={{ fontSize: 10.5, fontWeight: 800 }}>
                                              {formatSubscoreValue(
                                                field,
                                                value,
                                                selectedChain?.scoreVersion
                                              )}
                                            </span>
                                          </span>
                                        );
                                      })}
                                    </div>
                                  </React.Fragment>
                                );
                              })}
                              {itemFilters.family === 'training'
                                ? trainingTaskTypeSummary(familyScore.finding)
                                : null}
                            </div>
                          </td>
                        </tr>
                        {isExpanded ? (
                          <tr data-testid="cleaning-item-details-row">
                            <td style={bodyCellStyle}></td>
                            <td colSpan={itemsTableColumnCount - 1} style={bodyCellStyle}>
                              <div
                                style={{
                                  display: 'flex',
                                  gap: 26,
                                  flexWrap: 'wrap',
                                  alignItems: 'flex-start',
                                }}
                              >
                                {SUBSCORE_GROUPS.filter(
                                  (group) =>
                                    itemFilters.family === 'combined' ||
                                    group.key === itemFilters.family
                                ).map((group) => {
                                  const entries = tableSubscoreMetaByGroup[group.key];
                                  if (entries.length === 0) return null;
                                  return (
                                    <div
                                      key={group.key}
                                      style={{
                                        display: 'grid',
                                        gap: 3,
                                        minWidth: 175,
                                        alignContent: 'start',
                                      }}
                                    >
                                      <span style={subscoreGroupCaptionBaseStyle}>
                                        <span style={subscoreGroupDotStyle(group.color)} />
                                        {group.label}
                                      </span>
                                      {entries.map((field) => {
                                        const value = subscoreValueForItem(item, field);
                                        const chipSeverity = subscoreChipSeverity(
                                          field,
                                          value,
                                          selectedChain?.scoreVersion
                                        );
                                        return (
                                          <div
                                            key={field.key}
                                            style={{
                                              display: 'flex',
                                              justifyContent: 'space-between',
                                              gap: 10,
                                              fontSize: 11,
                                            }}
                                          >
                                            <span style={{ color: 'var(--text-secondary)' }}>
                                              {subscoreIndexLabel(field)} · {field.full}
                                            </span>
                                            <span
                                              style={{
                                                fontFamily: 'var(--font-mono)',
                                                fontWeight: 800,
                                                color: SUBSCORE_CHIP_COLORS[chipSeverity],
                                              }}
                                            >
                                              {formatSubscoreValue(
                                                field,
                                                value,
                                                selectedChain?.scoreVersion
                                              )}
                                            </span>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  );
                                })}
                                {linkScore.finding?.message ? (
                                  <div
                                    data-testid="cleaning-item-finding"
                                    style={{
                                      display: 'grid',
                                      gap: 3,
                                      minWidth: 175,
                                      maxWidth: 320,
                                      alignContent: 'start',
                                    }}
                                  >
                                    <span style={subscoreGroupCaptionBaseStyle}>
                                      Finding
                                      {trainingIssueMetadata(linkScore.finding.issueType)
                                        ? ` · ${trainingIssueMetadata(linkScore.finding.issueType)?.label}`
                                        : ''}
                                      {linkScore.finding.issueScope &&
                                      TRAINING_ISSUE_SCOPE_LABELS[linkScore.finding.issueScope]
                                        ? ` · ${TRAINING_ISSUE_SCOPE_LABELS[linkScore.finding.issueScope]}`
                                        : ''}
                                    </span>
                                    <strong
                                      style={{
                                        fontSize: 12,
                                        lineHeight: 1.35,
                                        color: 'var(--text-primary)',
                                      }}
                                    >
                                      {findingVerdict(
                                        linkScore.finding,
                                        linkScore.worstSubscore
                                      )}
                                    </strong>
                                    {masterLabelDetailsSummary ? (
                                      <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                                        {masterLabelDetailsSummary}
                                      </span>
                                    ) : null}
                                    {masterLabelUrl ? (
                                      <MasterLabelLink
                                        to={masterLabelUrl}
                                        variant="forge"
                                        title={masterLabelDescription}
                                        aria-label={`${findingVerdict(
                                          linkScore.finding,
                                          linkScore.worstSubscore
                                        )} Review in Master Label`}
                                        style={{
                                          width: 'fit-content',
                                          marginTop: 3,
                                          fontSize: 11,
                                          fontWeight: 850,
                                          textDecoration: 'none',
                                        }}
                                      >
                                        Review in Master Label →
                                      </MasterLabelLink>
                                    ) : null}
                                  </div>
                                ) : null}
                                <div
                                  data-testid="cleaning-item-counts"
                                  style={{
                                    display: 'grid',
                                    gap: 3,
                                    minWidth: 130,
                                    alignContent: 'start',
                                  }}
                                >
                                  <span style={subscoreGroupCaptionBaseStyle}>Counts</span>
                                  {countRows.map(([label, value]) => (
                                    <div
                                      key={label}
                                      style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        gap: 10,
                                        fontSize: 11,
                                      }}
                                    >
                                      <span style={{ color: 'var(--text-secondary)' }}>
                                        {label}
                                      </span>
                                      <span
                                        style={{ fontFamily: 'var(--font-mono)', fontWeight: 800 }}
                                      >
                                        {formatNumber(value)}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                                <div style={{ display: 'grid', gap: 5, alignContent: 'start' }}>
                                  <span style={subscoreGroupCaptionBaseStyle}>Actions</span>
                                  <button
                                    type="button"
                                    className="dg-secondary-button"
                                    disabled={isUpdatingExclusion || selectedChainIsOldVersion}
                                    title={
                                      selectedChainIsOldVersion
                                        ? 'Refresh this chain before changing item status'
                                        : undefined
                                    }
                                    onClick={(event) => {
                                      void handleToggleItemExcluded(event, item);
                                    }}
                                  >
                                    {isUpdatingExclusion
                                      ? item.excluded
                                        ? 'Reopening…'
                                        : 'Marking…'
                                      : item.excluded
                                        ? 'Reopen item'
                                        : 'No changes needed'}
                                  </button>
                                </div>
                                {evidenceCrops.length > 0 ? (
                                  <div style={{ display: 'grid', gap: 4, alignContent: 'start' }}>
                                    <span style={subscoreGroupCaptionBaseStyle}>
                                      Evidence crops
                                    </span>
                                    <div
                                      style={{
                                        display: 'flex',
                                        gap: 6,
                                        flexWrap: 'wrap',
                                        maxWidth: 430,
                                      }}
                                    >
                                      {evidenceCrops.map((crop) => (
                                        <img
                                          key={crop.cropId}
                                          src={crop.vizUrl}
                                          alt={crop.cropId}
                                          loading="lazy"
                                          style={evidenceThumbStyle}
                                        />
                                      ))}
                                    </div>
                                  </div>
                                ) : null}
                              </div>
                            </td>
                          </tr>
                        ) : null}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 8,
              marginTop: 10,
              fontSize: 11,
              color: 'var(--text-tertiary)',
            }}
          >
            <span>
              Page {formatNumber(itemFilters.page)} of {formatNumber(totalPages)} ·{' '}
              {formatNumber(itemsTotal)} items
            </span>
            <button
              type="button"
              aria-label="Previous page"
              style={pageButtonStyle}
              disabled={itemFilters.page <= 1 || itemsLoading || isItemsUpdating}
              onClick={handlePrevPage}
            >
              <ChevronLeftOutlinedIcon style={{ fontSize: 16 }} />
            </button>
            <button
              type="button"
              aria-label="Next page"
              style={pageButtonStyle}
              disabled={itemFilters.page >= totalPages || itemsLoading || isItemsUpdating}
              onClick={handleNextPage}
            >
              <ChevronRightOutlinedIcon style={{ fontSize: 16 }} />
            </button>
          </div>
        </div>
      </section>
        );
        return (
          <>
            {overviewSections}
            {taskQueueSection}
          </>
        );
        })()
      }

      {isScoreExplainerOpen ? (
        <ScoreExplainerDialog
          onClose={() => setIsScoreExplainerOpen(false)}
          scoreVersion={currentScoreVersion}
        />
      ) : null}
    </div>
  );
};
